import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Building,
  Check,
  ChevronDown,
  CreditCard,
  Eye,
  EyeOff,
  Globe,
  Loader2,
  Lock,
  Mail,
  Phone,
  Shield,
  Sparkles,
  User
} from 'lucide-react';
import XpheriumLogo from './XpheriumLogo';
import { AuthBelowFold, AuthHero, AuthTopNav } from './AuthFrontDoor';
import { startStripeSetupForUpgrade } from '../../utils/upgradePaymentFlow';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CheckoutElementsProvider, PaymentElement, useCheckout } from '@stripe/react-stripe-js/checkout';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useTranslation, Trans } from 'react-i18next';
import { isDesktopRuntime, navigateOrRedirect } from '../../utils/desktopNavigation';
import { getProductLandingUrl } from '../../utils/productRouting';
import { getApiBaseCandidates as listApiBaseCandidates } from '../../utils/apiBase';
import { markFirstSignupHome } from '../../utils/signupLanding';
import OnboardingQuestions from '../Onboarding/OnboardingQuestions';
import { isPartnerHost } from '../../utils/realm';
import {
  captureReferralFromUrl,
  getReferralSignupPayload,
  getStoredReferralCode,
  clearStoredReferral,
} from '../../utils/referralAttribution';
import {
  captureAttributionFromUrl,
  getSignupAttributionPayload,
  clearStoredAttribution,
} from '../../utils/signupAttribution';

const API_BASE_URL = process.env.REACT_APP_API_URL
  || (process.env.REACT_APP_RELEASE_CHANNEL === 'staging' ? '' : 'https://api.xpherium.com/api');
const PRODUCT_KEY = 'intelligence-navigator';
const PRODUCT_NAME = 'Intelligence Navigator';

// ──────────────────────────────────────────────────────────────────────
// V2 plan catalog (May 2026). The keys here MUST match
// backend PRICING_PLANS['intelligence-navigator']; the signup flow
// passes `plan: <key>` to /verification/verify and
// /verification/complete-registration and the backend uses that to
// look up the right Stripe price + provision the right subscription.
//
// This block is duplicated (intentionally — different codebase) from
// landing/src/app/intelligence-navigator/page.tsx. If you change the
// catalog here, change it there too.
// ──────────────────────────────────────────────────────────────────────
const PLAN_KEYS = ['free', 'starter', 'professional', 'proMax'];

const PLAN_PRICING = {
  free: { monthly: 0, yearly: 0, label: 'Free' },
  starter: { monthly: 11.99, yearly: 9.59, label: 'Starter' },
  professional: { monthly: 99, yearly: 79.20, label: 'Professional' },
  proMax: { monthly: 249, yearly: 199.20, label: 'ProMax' },
};

const PLAN_TAGLINES = {
  free: 'Hobby projects, light analytics',
  starter: '14-day trial, then $11.99/mo',
  professional: '14-day trial, then $99/mo',
  proMax: '14-day trial, then $249/mo',
};

// Bullet highlights shown on the dedicated plan picker screen
// (`view === 'plan'`). Kept short and parallel to the marketing
// site signup so both entry points read identically.
const PLAN_HIGHLIGHTS = {
  free: [
    'All 7 analytics modules',
    'CSV / Excel uploads',
    '200K AI credits / month',
  ],
  starter: [
    'Everything in Free',
    'Automation modules + AI Tools',
    'Vision AI (Beta)',
    'Google Sheets, OneDrive',
  ],
  professional: [
    'Everything in Starter',
    'AI Chatbots (external)',
    'Full CRM / SaaS connectors',
  ],
  proMax: [
    'Everything in Professional',
    'Highest limits across the board',
    '14-day trial, then $199.20/mo',
  ],
};

const isPaidPlan = (plan) => plan && plan !== 'free';

function readAuthSearch() {
  if (typeof window === 'undefined') return null;
  try {
    return new URL(window.location.href).searchParams;
  } catch {
    return null;
  }
}

function paidPlanFromSearch(params) {
  const plan = params?.get('plan') || '';
  return PLAN_KEYS.includes(plan) && plan !== 'free' ? plan : null;
}

function emailFromSignupTicket(ticket) {
  try {
    const part = String(ticket || '').split('.')[1];
    if (!part) return '';
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return String(json.email || '').toLowerCase();
  } catch {
    return '';
  }
}
const planHasTrial = (plan) => plan === 'starter' || plan === 'professional' || plan === 'proMax';
const TRIAL_DEFAULT_PLAN = 'professional';

function formatPlanPrice(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '$0';
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
}

function formatCharge(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '$0.00';
  const [whole, cents] = value.toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${grouped}.${cents}`;
}

const TRIAL_PLAN_OPTIONS = ['starter', 'professional', 'proMax'];

function TrialPlanPicker({ selectedPlan, billingCycle, onPlanChange, onCycleChange, children }) {
  const { t } = useTranslation('onboarding');
  const yearly = billingCycle !== 'monthly';
  const activePlan = PLAN_PRICING[selectedPlan] ? selectedPlan : TRIAL_DEFAULT_PLAN;

  return (
    <div className="mx-auto w-full max-w-[440px]">
      <div className="mb-4 flex items-center justify-center gap-3 text-[13px] text-[#6b7280]">
        <button
          type="button"
          onClick={() => onCycleChange?.('yearly')}
          className={yearly ? 'font-semibold text-[#16161d]' : 'hover:text-[#16161d]'}
        >
          {t('welcomeFlow.payment.annual')}
        </button>
        <span aria-hidden="true">|</span>
        <button
          type="button"
          onClick={() => onCycleChange?.('monthly')}
          className={!yearly ? 'font-semibold text-[#16161d]' : 'hover:text-[#16161d]'}
        >
          {t('welcomeFlow.payment.monthly')}
        </button>
      </div>

      <div className="mb-4 rounded-2xl bg-[#e7f6ee] px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="rounded-full bg-white px-3 py-1 text-[13px] font-medium text-[#16161d]">
            {t('welcomeFlow.payment.trialLabel')}
          </span>
          <span className="text-[15px] font-semibold text-[#16161d]">{t('welcomeFlow.payment.zeroToday')}</span>
        </div>
      </div>

      <div className="space-y-3">
        {TRIAL_PLAN_OPTIONS.map((plan) => {
          const meta = PLAN_PRICING[plan];
          const selected = plan === activePlan;
          const popular = plan === 'professional';
          const monthlyValue = formatPlanPrice(meta.monthly);
          return (
            <button
              key={plan}
              type="button"
              onClick={() => onPlanChange?.(plan)}
              aria-pressed={selected}
              className={`relative w-full rounded-2xl bg-white px-4 py-4 text-left transition ${
                selected ? 'border-2 border-[#16161d]' : 'border border-[#e7e7ea] hover:border-[#c8c8ce]'
              }`}
            >
              {popular && (
                <span className="absolute -top-2.5 left-4 rounded-md bg-[#16161d] px-2 py-0.5 text-[10px] font-semibold text-white">
                  {t('welcomeFlow.payment.mostPopular')}
                </span>
              )}
              <span className="flex items-center gap-3">
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                  selected ? 'border-[#16161d]' : 'border-[#d0d0d4]'
                }`}>
                  {selected && <span className="h-2.5 w-2.5 rounded-full bg-[#16161d]" />}
                </span>
                <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold text-[#16161d]">
                      {t(`welcomeFlow.payment.plans.${plan}`)}
                    </span>
                    <span className="mt-0.5 block text-[13px] text-[#6b7280]">{t('welcomeFlow.payment.trialLabel')}</span>
                    <span className="mt-1 block text-[13px] text-[#6b7280]">
                      <span className="line-through">{monthlyValue}</span>
                      <span className="mx-1.5 text-[#16161d]">→</span>
                      <span className="font-medium text-[#16161d]">{t('welcomeFlow.payment.zeroToday')}</span>
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-[12px] text-[#b0b4bd] line-through">{monthlyValue}</span>
                    <span className="block text-[28px] font-semibold leading-none tracking-tight text-[#16161d]">$0</span>
                    <span className="mt-0.5 block text-[12px] text-[#6b7280]">{t('welcomeFlow.payment.today')}</span>
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4">{children}</div>

      <p className="mt-4 flex items-center justify-center gap-1.5 text-[12px] text-[#8b93a7]">
        <Lock className="h-3.5 w-3.5" />
        {t('welcomeFlow.payment.paySecure')}
      </p>
    </div>
  );
}

// Stripe is loaded once and reused. If the publishable key is missing
// at build time, the paid-plan code path surfaces a friendly error
// before the user gets to the card field — the Free signup path is
// unaffected.
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;
// Person-on-laptop hero photo for partner auth (Google-style overlap layout).
const PARTNER_AUTH_HERO = '/images/partner-auth-hero.png';
const API_BASE_STORAGE_KEY = 'xph_api_base_url';
const isLocalApiBase = (apiBase = '') => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(String(apiBase || ''));
const isLocalRuntimeHost = () => {
  if (typeof window === 'undefined') return false;
  const host = String(window.location?.hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1';
};
const shouldIncludeLocalCandidates = () =>
  process.env.REACT_APP_ENABLE_LOCAL_API_FALLBACK === 'true' || isLocalRuntimeHost();

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.55-.2-2.27H12v4.29h6.44a5.5 5.5 0 0 1-2.39 3.61v3h3.87c2.27-2.09 3.57-5.17 3.57-8.63z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.87-3A7.2 7.2 0 0 1 12 19.3a7.17 7.17 0 0 1-6.72-4.95H1.28v3.09A12 12 0 0 0 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.28 14.35a7.2 7.2 0 0 1 0-4.7V6.56H1.28a12 12 0 0 0 0 10.88l4-3.09z"
    />
    <path
      fill="#EA4335"
      d="M12 4.77c1.76 0 3.34.61 4.58 1.82l3.43-3.43C17.95 1.24 15.23 0 12 0A12 12 0 0 0 1.28 6.56l4 3.09A7.17 7.17 0 0 1 12 4.77z"
    />
  </svg>
);

const ACCENT = {
  gradient: 'from-indigo-600 to-violet-600',
  gradientHover: 'hover:from-indigo-700 hover:to-violet-700',
  shadow: 'shadow-indigo-200/50',
  ring: 'focus:ring-indigo-500',
  text: 'text-indigo-600',
  textHover: 'hover:text-indigo-700',
  bg: 'bg-indigo-50'
};

// ──────────────────────────────────────────────────────────────────────
// Stripe card-collection step.
//
// Mirrors the inline payment step on the marketing site so the dashboard
// signup at app.xpherium.com looks and behaves identically when a paid
// plan is picked. Renders a single CardElement, confirms the
// SetupIntent on submit, and bubbles the resulting paymentMethodId back
// to the parent so /verification/complete-registration can create the
// actual Stripe Subscription.
//
// The `Elements` provider needs a `stripe` promise, so we split the
// component into an Inner (uses `useStripe()` / `useElements()`) and an
// outer wrapper that gates on `stripePromise` being non-null (i.e. the
// publishable key was supplied at build time).
// ──────────────────────────────────────────────────────────────────────
const cardElementOptions = {
  style: {
    base: {
      fontSize: '15px',
      color: '#111827',
      fontFamily: 'system-ui, sans-serif',
      '::placeholder': { color: '#9CA3AF' },
    },
    invalid: { color: '#DC2626' },
  },
  hidePostalCode: false,
};

const SIGNUP_WASH = {
  background: 'linear-gradient(110deg, #eef4fb 0%, #ecf2fb 42%, #f2f6fc 70%, #f8fbfe 100%)',
};
const SIGNUP_GLOW = {
  background: 'radial-gradient(115% 80% at 0% 0%, rgba(222,196,236,0.55) 0%, rgba(222,196,236,0.18) 32%, transparent 58%)',
};

const PaymentStepInner = ({
  email,
  name,
  selectedPlan,
  billingCycle,
  loading,
  error,
  onSuccess,
  onError,
  onBack,
  onCycleChange,
  clientSecret,
  cardTrial = false,
  embedded = false,
  onBindConfirm,
  onCardComplete,
  onPlanChange,
  cardSaved = false,
}) => {
  const { t } = useTranslation(['auth', 'onboarding']);
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const planInfo = PLAN_PRICING[selectedPlan];
  const trialPlan = planHasTrial(selectedPlan);
  const displayedPrice = planInfo
    ? (billingCycle === 'yearly' ? planInfo.yearly : planInfo.monthly)
    : 0;

  const confirmCard = async () => {
    if (!stripe || !elements) return null;
    const cardEl = elements.getElement(CardElement);
    if (!cardEl) {
      onError('Card field not ready. Please try again.');
      return null;
    }
    setSubmitting(true);
    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardEl,
          billing_details: { email, name },
        },
      });
      if (result.error) {
        onError(result.error.message || 'Card could not be saved');
        return null;
      }
      const paymentMethodId =
        typeof result.setupIntent?.payment_method === 'string'
          ? result.setupIntent.payment_method
          : result.setupIntent?.payment_method?.id;
      if (!paymentMethodId) {
        onError('Stripe did not return a payment method. Please try again.');
        return null;
      }
      if (!embedded) await onSuccess(paymentMethodId);
      return paymentMethodId;
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Payment failed');
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRef = useRef(confirmCard);
  confirmRef.current = confirmCard;
  useEffect(() => {
    if (!onBindConfirm) return undefined;
    onBindConfirm(() => confirmRef.current());
    return undefined;
  }, [onBindConfirm, stripe, elements, clientSecret]);

  useEffect(() => {
    onCardComplete?.(cardComplete);
  }, [cardComplete, onCardComplete]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (embedded) return;
    await confirmCard();
  };

  if (!planInfo) return null;

  if (embedded && cardTrial) {
    return (
      <TrialPlanPicker
        selectedPlan={selectedPlan}
        billingCycle={billingCycle}
        onPlanChange={onPlanChange}
        onCycleChange={onCycleChange}
      >
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}
        {cardSaved ? (
          <p className="text-center text-[15px] text-slate-600">{t('onboarding:welcomeFlow.payment.saved')}</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              <span className="inline-flex items-center gap-1.5">
                <CreditCard className="h-4 w-4 text-slate-400" /> {t('onboarding:welcomeFlow.payment.cardDetails')}
              </span>
            </label>
            <div className="rounded-xl border border-slate-200 bg-white p-3 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/30">
              <CardElement
                options={cardElementOptions}
                onChange={(event) => setCardComplete(Boolean(event.complete))}
              />
            </div>
          </form>
        )}
      </TrialPlanPicker>
    );
  }

  const yearly = billingCycle === 'yearly';
  const monthlyRate = formatPlanPrice(yearly ? planInfo.yearly : planInfo.monthly);
  const afterTrial = yearly
    ? t('auth:trial.monthlyBilledAnnually', { price: monthlyRate })
    : t('auth:trial.perMonth', { price: monthlyRate });

  const summary = (
    <>
      {trialPlan && (
        <div className="mt-1 flex items-center justify-center gap-2 text-[13px] text-slate-500">
          <button
            type="button"
            onClick={() => onCycleChange?.('yearly')}
            className={yearly ? 'font-semibold text-slate-900' : ''}
          >
            Annual
          </button>
          <span aria-hidden="true">|</span>
          <button
            type="button"
            onClick={() => onCycleChange?.('monthly')}
            className={!yearly ? 'font-semibold text-slate-900' : ''}
          >
            Monthly
          </button>
        </div>
      )}

      <div className={`rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${embedded ? 'mt-2' : 'mt-4'}`}>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{planInfo.label}</span>
          <span className="font-medium text-slate-900">{cardTrial ? t('auth:trial.badge') : trialPlan ? '14-day free trial' : `$${displayedPrice}/mo`}</span>
        </div>
        <div className="mt-2 flex items-center justify-between text-sm">
          <span className="text-slate-500">Due today</span>
          <span className="text-lg font-semibold text-emerald-700">{trialPlan ? '$0.00' : `$${displayedPrice}`}</span>
        </div>
        {trialPlan && (
          <div className="mt-2 flex items-start justify-between gap-4 border-t border-slate-200 pt-2 text-sm">
            <span className="text-slate-500">{cardTrial ? t('auth:trial.afterTrial') : 'After trial'}</span>
            <span className="text-right font-medium text-slate-900">{afterTrial}</span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            <span className="inline-flex items-center gap-1.5">
              <CreditCard className="h-4 w-4 text-slate-400" /> Card details
            </span>
          </label>
          <div className="rounded-xl border border-slate-200 bg-white p-3 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/30">
            <CardElement
              options={cardElementOptions}
              onChange={(event) => setCardComplete(Boolean(event.complete))}
            />
          </div>
        </div>

        {!embedded && (
          <button
            type="submit"
            disabled={!stripe || submitting || loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-[13px] font-semibold text-white shadow-[0_2px_8px_rgba(37,99,235,0.35)] transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            {(submitting || loading) && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {cardTrial ? t('auth:trial.startCta') : trialPlan ? 'Start 14-day trial' : `Subscribe — $${displayedPrice}/mo`}
          </button>
        )}

        <p className="inline-flex w-full items-center justify-center gap-1.5 text-center text-xs text-slate-400">
          <Shield className="h-3 w-3" />
          {cardTrial
            ? t('auth:trial.disclosure', {
                plan: planInfo.label,
                price: yearly ? `${monthlyRate}/mo` : `${monthlyRate}/month`,
                cadence: yearly ? t('auth:trial.cadenceAnnually') : t('auth:trial.cadenceMonthly'),
              })
            : 'Card details are processed securely by Stripe. Cancel anytime.'}
        </p>
      </form>
    </>
  );

  if (embedded) {
    return (
      <div className="mx-auto w-full max-w-xl">
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}
        {summary}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={onBack}
        type="button"
        className="mb-6 text-[13px] font-medium text-slate-500 hover:text-slate-800"
      >
        ← Back
      </button>
      <h1 className="text-[26px] font-bold tracking-tight text-slate-900">
        {cardTrial ? t('auth:trial.readyTitle') : trialPlan ? '$0 today' : `Subscribe to ${planInfo.label}`}
      </h1>
      <p className="mt-2 text-[15px] leading-relaxed text-slate-500">
        {cardTrial
          ? t('auth:trial.readyBody')
          : trialPlan
          ? '14-day free trial. Cancel anytime. Add a card so the plan can continue after day 14. You will not be charged today.'
          : `Add a card to activate your ${planInfo.label} subscription.`}
      </p>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {summary}
    </div>
  );
};

function CustomCardFields({ active, onRegisterConfirm, onCardComplete, onError, onReady }) {
  const checkoutState = useCheckout();
  const tokenRef = useRef({});
  const completeRef = useRef(false);
  const checkoutRef = useRef(null);
  const onRegisterRef = useRef(onRegisterConfirm);
  const onErrorRef = useRef(onError);
  const onCardCompleteRef = useRef(onCardComplete);
  const onReadyRef = useRef(onReady);
  onRegisterRef.current = onRegisterConfirm;
  onErrorRef.current = onError;
  onCardCompleteRef.current = onCardComplete;
  onReadyRef.current = onReady;
  if (checkoutState.type === 'success') checkoutRef.current = checkoutState.checkout;

  useEffect(() => {
    if (!active) return undefined;
    onCardCompleteRef.current?.(completeRef.current);
    if (checkoutState.type !== 'success') return undefined;
    const token = tokenRef.current;
    onRegisterRef.current?.(async () => {
      const checkout = checkoutRef.current;
      if (!checkout) return false;
      const result = await checkout.confirm({ redirect: 'if_required' });
      if (result.type === 'error') {
        onErrorRef.current?.(result.error?.message || 'Could not save the card.');
        return false;
      }
      return true;
    }, token);
    return () => onRegisterRef.current?.(null, token);
  }, [active, checkoutState.type]);

  if (checkoutState.type === 'loading') {
    return <div className="h-[136px] animate-pulse rounded-xl bg-[#f4f5f7]" />;
  }
  if (checkoutState.type === 'error') {
    return <p className="text-center text-sm text-red-700">{checkoutState.error.message}</p>;
  }
  return (
    <PaymentElement
      onReady={() => onReadyRef.current?.()}
      onChange={(event) => {
        completeRef.current = Boolean(event.complete || !event.empty);
        if (active) onCardCompleteRef.current?.(completeRef.current);
      }}
    />
  );
}

function SmoothCustomCard({ clientSecret, onRegisterConfirm, onCardComplete, onError }) {
  const secretRef = useRef(clientSecret);
  secretRef.current = clientSecret;
  const [layers, setLayers] = useState([{ secret: clientSecret, visible: true }]);

  useEffect(() => {
    setLayers((current) => {
      if (current.some((layer) => layer.visible && layer.secret === clientSecret)) {
        return current.filter((layer) => layer.secret === clientSecret);
      }
      const visible = current.find((layer) => layer.visible);
      const next = visible && visible.secret !== clientSecret ? [visible] : [];
      if (!current.some((layer) => layer.secret === clientSecret)) {
        next.push({ secret: clientSecret, visible: next.length === 0 });
      }
      return next;
    });
  }, [clientSecret]);

  const reveal = (secret) => {
    if (secret !== secretRef.current) return;
    setLayers([{ secret, visible: true }]);
  };

  return (
    <div className="relative min-h-[168px] rounded-2xl border border-[#e7e7ea] bg-white">
      {layers.map((layer) => (
        <div
          key={layer.secret}
          className={layer.visible ? 'relative px-4 py-4' : 'pointer-events-none absolute inset-0 px-4 py-4 opacity-0'}
          aria-hidden={!layer.visible}
        >
          <CheckoutElementsProvider stripe={stripePromise} options={{ clientSecret: layer.secret }}>
            <CustomCardFields
              active={layer.visible}
              onRegisterConfirm={onRegisterConfirm}
              onCardComplete={onCardComplete}
              onError={onError}
              onReady={() => {
                if (!layer.visible) reveal(layer.secret);
              }}
            />
          </CheckoutElementsProvider>
        </div>
      ))}
    </div>
  );
}

const LocalizedTrialCheckout = ({
  clientSecret,
  onComplete,
  selectedPlan,
  billingCycle,
  onPlanChange,
  onCycleChange,
  error,
  cardSaved,
  uiMode = 'embedded',
  onRegisterConfirm,
  onCardComplete,
  onError,
}) => {
  const { t } = useTranslation('onboarding');
  return (
  <TrialPlanPicker
    selectedPlan={selectedPlan}
    billingCycle={billingCycle}
    onPlanChange={onPlanChange}
    onCycleChange={onCycleChange}
  >
    {error && (
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        {error}
      </div>
    )}
    {cardSaved ? (
      <p className="text-center text-[15px] text-slate-600">{t('welcomeFlow.payment.saved')}</p>
    ) : uiMode === 'custom' && clientSecret ? (
      <SmoothCustomCard
        clientSecret={clientSecret}
        onRegisterConfirm={onRegisterConfirm}
        onCardComplete={onCardComplete}
        onError={onError}
      />
    ) : clientSecret ? (
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret, onComplete }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    ) : (
      <div className="h-[168px] animate-pulse rounded-2xl border border-[#e7e7ea] bg-white" />
    )}
  </TrialPlanPicker>
  );
};

const PaymentStep = (props) => {
  if (props.localizedCheckout) {
    return <LocalizedTrialCheckout {...props} />;
  }
  if (!stripePromise) {
    return (
      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          Payment is not configured on this site. Please contact support.
        </div>
      </div>
    );
  }
  // Bind the Elements provider to the SetupIntent client secret so the
  // card form can confirm against it. The provider remounts whenever
  // the client secret changes, which is fine — each paid signup gets a
  // fresh SetupIntent.
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: props.clientSecret }}>
      <PaymentStepInner {...props} />
    </Elements>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['partner', 'auth', 'onboarding']);
  const signupLanguage = () => {
    const lang = String(i18n.language || 'en').split('-')[0];
    return ['en', 'pt', 'fr', 'es'].includes(lang) ? lang : 'en';
  };
  const { loading: authLoading, isAuthenticated, user, logout } = useAuth();
  const { loading: onboardingLoading } = useOnboarding();

  // Initial view honors `?view=signup` / `?view=signin` so the Partner Program
  // landing page (and any external share link) can deep-link straight into
  // the signup form instead of starting on signin. A `?plan=<key>` deep
  // link also forces the view to 'signup' (the URL reader below flips it).
  const [view, setView] = useState(() => {
    if (typeof window === 'undefined') return 'signin';
    try {
      const u = new URL(window.location.href);
      const v = u.searchParams.get('view');
      const planParam = u.searchParams.get('plan');
      if (v === 'signup-questions' && u.searchParams.get('ticket')) {
        return paidPlanFromSearch(u.searchParams) ? 'payment' : 'questions';
      }
      // Paid Google return and the pricing-page trial handoff must open
      // the payment step on the first paint. Treating `?plan=` as signup
      // here used to win the race and leave a blank card, or skip the
      // card step and dump the visitor on the dashboard.
      if (v === 'payment' && paidPlanFromSearch(u.searchParams)) return 'payment';
      if (u.searchParams.get('startTrial') === '1' && paidPlanFromSearch(u.searchParams)) return 'signin';
      if (planParam && PLAN_KEYS.includes(planParam)) return 'signup';
      return v === 'signup' ? 'signup' : 'signin';
    } catch {
      return 'signin';
    }
  });
  // Persist affiliate-intent across the multi-step signup so we can auto-call
  // POST /api/affiliates/join right after email verification creates the
  // Client row. Cleared on any successful sign-in/up that doesn't carry it.
  const [shouldAutoEnrollAffiliate, setShouldAutoEnrollAffiliate] = useState(false);

  // ──────────────────────────────────────────────────────────────────
  // V2 plan selection (May 27 2026). Mirrors the marketing site signup.
  //
  // `selectedPlan` defaults to null — no plan pre-selected — to force
  // every visitor through the on-page picker. Used to default to 'free'
  // which silently landed everyone on Free unless they clicked through
  // from the pricing page with `?plan=` in the URL.
  //
  // `billingCycle` defaults to yearly. Professional and Pro Max show the
  // 20%-off per-month rate. Starter annual is $9.59 ($11.99 × 0.8).
  //
  // `stripeCustomerId` / `stripeClientSecret` are populated after the
  // email verification step for paid plans — they feed PaymentStep.
  // ──────────────────────────────────────────────────────────────────
  const [selectedPlan, setSelectedPlan] = useState(() => paidPlanFromSearch(readAuthSearch()));
  const [billingCycle, setBillingCycle] = useState(() => {
    const billing = readAuthSearch()?.get('billing');
    return billing === 'monthly' ? 'monthly' : 'yearly';
  });
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [pendingGooglePayment, setPendingGooglePayment] = useState(() => {
    const params = readAuthSearch();
    return params?.get('view') === 'payment' && Boolean(paidPlanFromSearch(params));
  });
  // Existing account clicked a paid plan on the pricing page, signed in
  // on the marketing site, and was handed here to open Stripe Checkout.
  const [trialHandoff, setTrialHandoff] = useState(() => {
    const params = readAuthSearch();
    if (params?.get('startTrial') !== '1') return null;
    return paidPlanFromSearch(params);
  });
  const [googleTicket, setGoogleTicket] = useState(() => {
    try {
      const u = new URL(window.location.href);
      return u.searchParams.get('view') === 'signup-questions'
        ? (u.searchParams.get('ticket') || '')
        : '';
    } catch {
      return '';
    }
  });
  const [heldPaymentMethodId, setHeldPaymentMethodId] = useState(null);
  const [heldCheckoutSessionId, setHeldCheckoutSessionId] = useState(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const heldPaymentRef = useRef(null);
  const heldCheckoutRef = useRef(null);
  const checkoutSessionRef = useRef(null);
  const checkoutUiModeRef = useRef('embedded');
  const checkoutKeyRef = useRef('');
  const confirmPaymentRef = useRef(null);
  const confirmOwnerRef = useRef(null);
  const billingCycleRef = useRef(billingCycle);
  billingCycleRef.current = billingCycle;
  const checkoutCacheRef = useRef({});
  const [checkoutUiMode, setCheckoutUiMode] = useState('embedded');
  const trialPlanRef = useRef(TRIAL_DEFAULT_PLAN);
  const trialPlanTouchedRef = useRef(false);
  const [cardFieldComplete, setCardFieldComplete] = useState(false);
  const [trialExperiment, setTrialExperiment] = useState(false);
  const [pendingSignupAnswers, setPendingSignupAnswers] = useState(null);
  // Mirrors the signup-side affiliate flag for the SIGNIN flow. When a
  // visitor lands here via /?view=signin&go=partner-portal we surface
  // Partner Portal branding instead of IN branding so the user never
  // feels like they were rerouted into the wrong product. The actual
  // post-auth route to /partner-portal is driven by `xph_post_auth_redirect`.
  const [wantsPartnerPortal, setWantsPartnerPortal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [preventSigninAutofill, setPreventSigninAutofill] = useState(true);
  const [signinFieldNonce, setSigninFieldNonce] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [signinMethod, setSigninMethod] = useState(null);
  const [signupEntry, setSignupEntry] = useState(null);
  const [error, setError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [requires2FA, setRequires2FA] = useState(false);
  const [tempUserId, setTempUserId] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [suggestPasswordReset, setSuggestPasswordReset] = useState(false);

  const [redirectUrl, setRedirectUrl] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPhone, setForgotPhone] = useState('');
  const [resetVerificationType, setResetVerificationType] = useState('email');
  const [resetCode, setResetCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const signinEmailRef = useRef(null);
  const signinPasswordRef = useRef(null);

  // Track the active referral code so we can render a confirmation badge
  // ("You've been referred — sign up to give them credit") above the
  // signup form. Updates after captureReferralFromUrl resolves.
  const [referralCode, setReferralCode] = useState(getStoredReferralCode());

  // Capture `?ref=` from the URL on first mount: validate it server-side,
  // log a click row, drop a 90-day cookie + localStorage, and strip the
  // query param from the URL so reshares from this user don't accidentally
  // re-attribute new visitors. captureReferralFromUrl is idempotent.
  //
  // Also captures `?affiliate=1` (set by the Partner Program landing page).
  // When present, the user has explicitly said "I want to join the affiliate
  // program on signup". We persist this intent across the verify step and
  // call POST /api/affiliates/join automatically after Client.create.
  //
  // The partner-mode flags MUST be set synchronously on mount — the auth
  // redirect effect below runs the moment auth state resolves and would
  // otherwise win the race and send the user to /dashboard before we know
  // they wanted /partner-portal. So we do the URL parse first, set state +
  // localStorage, then kick off the async referral attribution work.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/subscriptions/experiment`)
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled) setTrialExperiment(data?.trialMode === 'card_required_30_day');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!trialExperiment || shouldAutoEnrollAffiliate || isPaidPlan(selectedPlan)) return;
    setSelectedPlan(TRIAL_DEFAULT_PLAN);
  }, [trialExperiment, selectedPlan, shouldAutoEnrollAffiliate]);

  const selectTrialPlan = (plan) => {
    if (heldCheckoutRef.current) return;
    if (!isPaidPlan(plan) || !PLAN_PRICING[plan]) return;
    trialPlanTouchedRef.current = true;
    trialPlanRef.current = plan;
    setSelectedPlan(plan);
  };

  const selectBillingCycle = (cycle) => {
    if (heldCheckoutRef.current) return;
    setBillingCycle(cycle === 'monthly' ? 'monthly' : 'yearly');
  };

  const rememberCheckout = (sessionId) => {
    if (!sessionId) return;
    heldCheckoutRef.current = sessionId;
    setHeldCheckoutSessionId(sessionId);
    try {
      sessionStorage.setItem('xph_trial_checkout', JSON.stringify({
        sessionId,
        email,
        plan: trialPlanRef.current || selectedPlan,
        billingCycle,
        firstName,
        lastName,
      }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let sessionId = '';
    try {
      sessionId = new URL(window.location.href).searchParams.get('trial_checkout') || '';
    } catch { return undefined; }
    if (!sessionId.startsWith('cs_')) return undefined;
    let snap = null;
    try { snap = JSON.parse(sessionStorage.getItem('xph_trial_checkout') || 'null'); } catch { snap = null; }
    if (snap?.email) setEmail(snap.email);
    if (snap?.firstName) setFirstName(snap.firstName);
    if (snap?.lastName) setLastName(snap.lastName);
    if (snap?.plan && PLAN_PRICING[snap.plan]) {
      trialPlanTouchedRef.current = true;
      trialPlanRef.current = snap.plan;
      setSelectedPlan(snap.plan);
    }
    if (snap?.billingCycle === 'monthly' || snap?.billingCycle === 'yearly') {
      setBillingCycle(snap.billingCycle);
    }
    heldCheckoutRef.current = sessionId;
    setHeldCheckoutSessionId(sessionId);
    setCheckoutSessionId(sessionId);
    setTrialExperiment(true);
    setView('questions');
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('trial_checkout');
      window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    } catch { /* ignore */ }
    return undefined;
  }, []);

  useEffect(() => {
    heldPaymentRef.current = heldPaymentMethodId;
  }, [heldPaymentMethodId]);

  useEffect(() => {
    if (!trialExperiment || !googleTicket || heldPaymentMethodId || pendingSignupAnswers) return;
    if (view === 'payment') setView('questions');
  }, [trialExperiment, googleTicket, heldPaymentMethodId, pendingSignupAnswers, view]);

  useEffect(() => {
    // ── 0. Legacy localStorage cleanup (May 27 2026) ──
    //       `xph_post_auth_redirect` and `xph_signup_join_affiliate` used
    //       to live in localStorage, which persisted across tabs and
    //       browser sessions forever. If a user once clicked Partner
    //       Sign-in or visited /affiliates and abandoned the flow, the
    //       flags were orphaned — every later visit to app.xpherium.com
    //       would silently flip the page into Partner Portal mode. We
    //       moved both flags to sessionStorage so they die when the tab
    //       closes (matching the OAuth round-trip lifecycle). This block
    //       wipes any stale localStorage copy so returning users with
    //       baked-in orphan flags self-heal on next visit.
    try { localStorage.removeItem('xph_post_auth_redirect'); } catch { /* ignore */ }
    try { localStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }

    // ── 0b. Stale `xph_session` cookie eviction (May 27 2026) ──
    //        The HttpOnly `xph_session` cookie lives on `.xpherium.com`
    //        and is shared across every product (Intelligence Navigator,
    //        Partner Portal, Knowledge IQ, Data IQ, etc.). A visitor
    //        who previously signed into the Partner Portal — or any
    //        other product that issues an `xph_session` — keeps that
    //        cookie indefinitely. When they later try to sign into
    //        Intelligence Navigator, the cookie still resolves to the
    //        original account (or a deleted test client), and a
    //        cookie-only request to `/auth/me` returns the wrong user;
    //        the dashboard either silently logs them in as the wrong
    //        identity or bounces them to /partner-portal.
    //
    //        The auth middleware now prefers the `Authorization` header
    //        over the cookie (see backend/src/utils/authCookie.js), so
    //        any request the dashboard makes with a fresh Bearer token
    //        wins. This call evicts the lingering cookie too, so even
    //        cookie-only navigations (HTML <a> clicks, OAuth redirects)
    //        don't keep hitting a dead token.
    //
    //        We only fire it when there's NO fresh `auth=` URL param —
    //        i.e. the page is rendering as the unauthenticated sign-in
    //        entry, not a post-signup redirect. Fire-and-forget; never
    //        block the sign-in render. Crucially we do NOT touch
    //        localStorage here — authenticated users who land on `/`
    //        directly need their tokens intact while the redirect
    //        effect below kicks them to /dashboard. AuthContext's
    //        background `validateToken` will clear localStorage itself
    //        if the token is genuinely dead.
    try {
      const hasFreshAuthParam = new URLSearchParams(window.location.search).has('auth');
      if (!hasFreshAuthParam) {
        fetch(`${API_BASE_URL}/auth/clear-session`, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
        }).catch(() => { /* best-effort cleanup */ });
      }
    } catch { /* never block sign-in render */ }

    // ── 1. Synchronous URL capture (partner-mode flags must be set before
    //       the auth-redirect effect fires — otherwise an already-signed-in
    //       customer arriving via the Partner Program page would flash IN
    //       chrome before being bounced to /partner-portal).
    try {
      const url = new URL(window.location.href);
      const wantsAffiliate = url.searchParams.get('affiliate') === '1';
      const goTarget = url.searchParams.get('go');
      // On the partner subdomain (partner.xpherium.com) the entire app IS the
      // Partner Portal — every auth screen is partner-branded and post-auth
      // routing lands in /partner-portal. Treat it as an implicit partner
      // sign-in intent so a bare visit (no ?go=) still renders correctly.
      const onPartnerHost = isPartnerHost();
      const isPartnerSigninIntent =
        goTarget === 'affiliate-program' || goTarget === 'partner-portal' || onPartnerHost;
      // An *explicit* IN intent (sent by the landing-site header's
      // Sign in button as `?go=intelligence-navigator`) overrides any
      // stranded partner-portal session flag from a previous visit
      // to /affiliates within the same browser tab. Without this guard
      // the dashboard would flip the sign-in form into Partner Portal
      // branding every time a returning visitor clicked Sign in from
      // /pricing, /blog, or any other XpheriumChrome page.
      const isIntelligenceNavigatorIntent = goTarget === 'intelligence-navigator';

      // A `?ref=` referral link is a CUSTOMER signup (someone an affiliate
      // referred), NEVER a partner-program join. If a partner-intent flag is
      // stranded in this tab from an earlier /affiliates visit, it must be
      // cleared here — otherwise the stale-flag re-arm below would silently
      // convert the referred customer into a free PARTNER account, skipping
      // the plan picker and payment entirely. Explicit partner intent
      // (?affiliate=1, ?go=partner*, or the partner subdomain) still wins.
      const hasRefParam = url.searchParams.has('ref');
      const customerReferralIntent = hasRefParam && !wantsAffiliate && !isPartnerSigninIntent;
      if (customerReferralIntent) {
        try { sessionStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }
        try { localStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }
        try { sessionStorage.removeItem('xph_post_auth_redirect'); } catch { /* ignore */ }
        setShouldAutoEnrollAffiliate(false);
        setWantsPartnerPortal(false);
      }

      if (wantsAffiliate) {
        setShouldAutoEnrollAffiliate(true);
        try { sessionStorage.setItem('xph_signup_join_affiliate', '1'); } catch { /* ignore */ }
        // Force the form into signup mode; ?affiliate=1 always implies "I
        // want to create a partner account".
        setView('signup');
      } else if (!customerReferralIntent) {
        try {
          if (sessionStorage.getItem('xph_signup_join_affiliate') === '1') {
            setShouldAutoEnrollAffiliate(true);
          }
        } catch { /* ignore */ }
      }

      // ── v2 plan deep-link ──
      //
      // Marketing site CTAs that point at app.xpherium.com can hint
      // the plan they want via `?plan=<key>` and (optionally) a
      // billing cycle via `?billing=monthly|yearly`. Both are
      // honored only when the value is in our known catalog so a
      // mistyped URL just renders the empty picker instead of
      // crashing or silently mapping to Free.
      const planParam = url.searchParams.get('plan');
      const viewParam = url.searchParams.get('view');
      if (viewParam === 'signup-questions' && url.searchParams.get('ticket')) {
        setGoogleTicket(url.searchParams.get('ticket'));
        setView(planParam && PLAN_KEYS.includes(planParam) && planParam !== 'free' ? 'payment' : 'questions');
      } else if (planParam && PLAN_KEYS.includes(planParam)) {
        setSelectedPlan(planParam);
        // A plan deep-link is unambiguously a signup intent — the
        // initial-view useState above already flips here, but we
        // make it explicit so the affiliate / partner branches
        // below don't accidentally re-route us into Sign In.
        // startTrial=1 is an existing account opening checkout, and
        // view=payment is the card step. Neither should become signup.
        if (viewParam !== 'payment' && url.searchParams.get('startTrial') !== '1') {
          setView('signup');
        }
      }
      if (viewParam === 'payment' && planParam && PLAN_KEYS.includes(planParam)) {
        setPendingGooglePayment(true);
        setView('payment');
      }
      const billingParam = url.searchParams.get('billing');
      if (billingParam === 'monthly' || billingParam === 'yearly') {
        setBillingCycle(billingParam);
      }

      if (isPartnerSigninIntent) {
        // Surface Partner Portal branding in the signin form …
        setWantsPartnerPortal(true);
        // … and arm the one-shot post-auth redirect consumed by both the
        // already-authenticated effect below AND ConditionalDashboard.
        try { sessionStorage.setItem('xph_post_auth_redirect', '/partner-portal'); } catch { /* ignore */ }
        // On the dedicated partner subdomain, a new signup is unambiguously a
        // partner signup — arm auto-enroll so they're enrolled after email
        // verification (without forcing the form into signup mode; returning
        // partners still default to sign-in).
        if (onPartnerHost) {
          setShouldAutoEnrollAffiliate(true);
          try { sessionStorage.setItem('xph_signup_join_affiliate', '1'); } catch { /* ignore */ }
        }
      } else if (isIntelligenceNavigatorIntent) {
        // Explicit IN intent: nuke any stranded partner-portal session
        // flags from a previous URL navigation within the same tab.
        try { sessionStorage.removeItem('xph_post_auth_redirect'); } catch { /* ignore */ }
        try { sessionStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }
        setWantsPartnerPortal(false);
        setShouldAutoEnrollAffiliate(false);
      } else {
        try {
          if (sessionStorage.getItem('xph_post_auth_redirect') === '/partner-portal') {
            setWantsPartnerPortal(true);
          }
        } catch { /* ignore */ }
      }

      let changed = false;
      // Keep the trial card step in the URL. Stripping it made a refresh,
      // or a second mount, look like sign-in and send an unfinished trial
      // into the free dashboard.
      ['affiliate', 'go'].forEach((key) => {
        if (url.searchParams.has(key)) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (changed) window.history.replaceState({}, document.title, url.toString());
    } catch {
      // Best-effort — never block the UI.
    }

    // ── 1c. Marketing acquisition capture (utm_*). First-touch, persisted to
    //        localStorage + a 90-day cookie. Independent of the affiliate
    //        `?ref=` system — no commission/banner, purely so we can attribute
    //        a signup (and its eventual plan) to the campaign that drove it.
    //        The app domain has no web analytics, so this is the only place
    //        these tags get recorded. Synchronous + best-effort.
    try { captureAttributionFromUrl(); } catch { /* never block sign-in render */ }

    // ── 2. Async referral attribution (?ref=). Safe to await afterwards
    //       because we no longer depend on it for routing.
    let cancelled = false;
    (async () => {
      try {
        const code = await captureReferralFromUrl();
        if (!cancelled) setReferralCode(code || null);
      } catch {
        // Attribution must never block UI.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effective partner-signup flag. A partner-only signup creates a FREE
  // affiliate account and skips the plan picker + payment. But if the visitor
  // has explicitly selected a PAID plan, they are unambiguously a paying
  // customer — we must honor that and never silently downgrade them into a
  // free partner account. This single invariant also neutralizes a stale
  // `xph_signup_join_affiliate` flag stranded from an earlier /affiliates
  // visit in the same tab: it can never hijack a paid signup.
  const enrollAsPartner = shouldAutoEnrollAffiliate && !isPaidPlan(selectedPlan);

  // Derived flag: any flow that should make this page read as the Partner
  // Portal rather than Intelligence Navigator. Drives all UI copy/branding
  // swaps. Updated whenever either signup OR signin intent flips on.
  const partnerMode = enrollAsPartner || wantsPartnerPortal;

  // The marketing site owns sign-in and sign-up. This route stays for
  // payment, verification, and the desktop app, but a logged-out browser
  // visit must not render a second sign-in screen.
  const leaveForMarketingSignin = (() => {
    if (process.env.REACT_APP_RELEASE_CHANNEL === 'staging') return false;
    if (authLoading || isAuthenticated || isDesktopRuntime || partnerMode || requires2FA) return false;
    if (view !== 'signin' && view !== 'signup') return false;
    if (trialHandoff) return false;
    let hasLocalAuthToken = false;
    try {
      hasLocalAuthToken = Boolean(localStorage.getItem('token') || localStorage.getItem('auth_token'));
    } catch { /* ignore */ }
    if (hasLocalAuthToken) return false;
    const params = readAuthSearch();
    if (!params) return false;
    if (params.has('auth')) return false;
    const urlView = params.get('view');
    if (urlView === 'payment' || urlView === 'signup-questions') return false;
    if (params.get('startTrial') === '1' || params.get('go') === 'partner-portal') return false;
    return true;
  })();

  useEffect(() => {
    if (!leaveForMarketingSignin) return;
    const params = readAuthSearch();
    const target = new URL(getProductLandingUrl('intelligence-navigator'));
    ['view', 'plan', 'billing', 'lang', 'ref', 'affiliate', 'resume'].forEach((key) => {
      const value = params?.get(key);
      if (value) target.searchParams.set(key, value);
    });
    if (view === 'signup' && !target.searchParams.get('view')) {
      target.searchParams.set('view', 'signup');
    }
    window.location.replace(target.toString());
  }, [leaveForMarketingSignin, view]);

  // Browser tab title — reflects the active flow. The default in
  // public/index.html is the Xpherium software title, which is misleading
  // when the visitor explicitly arrived on the Partner Portal sign-in/up
  // page. We override it here and restore the IN default on unmount so
  // navigating away (back to /dashboard, /onboarding, etc.) lands on
  // the correct title.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previousTitle = document.title;
    document.title = partnerMode
      ? t('partner:auth.documentTitle')
      : 'Analytics and Business Insights Software';
    return () => {
      document.title = previousTitle;
    };
  }, [partnerMode, t]);

  useEffect(() => {
    if (!authLoading && !onboardingLoading && isAuthenticated && user) {
      if (view === 'questions' || view === 'payment') return;
      if (trialHandoff) return;
      const isAutopilotOnly =
        user.signupProduct === 'autopilot' && !user.productAccess?.includes('intelligence-navigator');
      if (isAutopilotOnly) {
        logout();
        alert('This account is registered for Autopilot only. Please use the Autopilot product at /autopilot');
        navigate('/autopilot', { replace: true });
        return;
      }

      // Partner-only accounts NEVER touch the IN dashboard. They get the
      // standalone Partner Portal as their entire app surface.
      if (user.accountType === 'partner') {
        navigate('/partner-portal', { replace: true });
        return;
      }

      // Customer arriving through the affiliate flow (?affiliate=1 or
      // ?go=partner-portal) — partnerMode was set synchronously on mount.
      // Honor that intent before any sessionStorage / dashboard fallback.
      if (partnerMode) {
        try { sessionStorage.removeItem('xph_post_auth_redirect'); } catch { /* ignore */ }
        try { localStorage.removeItem('xph_post_auth_redirect'); } catch { /* ignore */ }
        navigate('/partner-portal', { replace: true });
        return;
      }

      // Fallback: a stored one-shot post-auth redirect set elsewhere
      // (e.g. crossRealmAuth.signOutAndRouteTo). sessionStorage-only —
      // see mount effect for the rationale.
      let target = null;
      try {
        target = sessionStorage.getItem('xph_post_auth_redirect');
        if (target) sessionStorage.removeItem('xph_post_auth_redirect');
      } catch { /* ignore */ }
      if (target && typeof target === 'string' && target.startsWith('/')) {
        navigate(target, { replace: true });
        return;
      }

      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, onboardingLoading, isAuthenticated, user, logout, navigate, partnerMode, pendingGooglePayment, view, trialHandoff]);

  // After Google OAuth with a paid plan, hydrate the Stripe SetupIntent once
  // the auth handoff has populated the user record.
  useEffect(() => {
    if (!pendingGooglePayment || view !== 'payment' || stripeClientSecret) return;
    if (!user?.email) return;

    setEmail(user.email);
    if (user.name) {
      const parts = String(user.name).trim().split(/\s+/);
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        if (!stripePromise) {
          throw new Error('Payment is not configured on this site. Please contact support.');
        }
        const intentRes = await fetch(`${API_BASE_URL}/subscriptions/create-signup-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: user.email,
            name: user.name || user.email,
            product: PRODUCT_KEY,
          }),
        });
        const intentData = await intentRes.json();
        if (!intentRes.ok) {
          throw new Error(intentData.error || 'Failed to initialize payment');
        }
        if (cancelled) return;
        setStripeClientSecret(intentData.clientSecret);
        setStripeCustomerId(intentData.stripeCustomerId);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to initialize payment');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingGooglePayment, view, stripeClientSecret, user]);

  // New Google trial signups collect the card before any account exists
  // and before the describe-yourself questions.
  useEffect(() => {
    if (view !== 'payment' || !googleTicket || stripeClientSecret || heldPaymentMethodId) return undefined;
    if (!isPaidPlan(selectedPlan)) return undefined;
    const ticketEmail = emailFromSignupTicket(googleTicket);
    if (!ticketEmail) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        if (!stripePromise) {
          throw new Error('Payment is not configured on this site. Please contact support.');
        }
        const intentRes = await fetch(`${API_BASE_URL}/subscriptions/create-signup-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: ticketEmail, name: ticketEmail, product: PRODUCT_KEY }),
        });
        const intentData = await intentRes.json().catch(() => ({}));
        if (!intentRes.ok) throw new Error(intentData.error || 'Failed to initialize payment');
        if (cancelled) return;
        setEmail(ticketEmail);
        setStripeClientSecret(intentData.clientSecret);
        setStripeCustomerId(intentData.stripeCustomerId);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to initialize payment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [view, googleTicket, stripeClientSecret, heldPaymentMethodId, selectedPlan]);

  // Trial signups open Stripe Checkout inside the describe-yourself flow.
  // The USD Price stays canonical. Stripe localizes it and authenticates.
  // Every plan is prepared while the earlier questions are on screen, so
  // changing plan swaps a form that is already loaded.
  useEffect(() => {
    if (view !== 'questions' || enrollAsPartner || !trialExperiment) return undefined;
    if (heldCheckoutRef.current) return undefined;
    const intentEmail = email || emailFromSignupTicket(googleTicket);
    if (!intentEmail) return undefined;
    let cancelled = false;
    const fullName = `${firstName} ${lastName}`.trim() || intentEmail;
    const showCached = (key, data) => {
      checkoutCacheRef.current[key] = data;
      const plan = trialPlanRef.current || TRIAL_DEFAULT_PLAN;
      const cycle = billingCycleRef.current === 'monthly' ? 'monthly' : 'yearly';
      if (key !== `${intentEmail}:${plan}:${cycle}`) return;
      if (checkoutSessionRef.current === data.sessionId && stripeClientSecret) return;
      checkoutKeyRef.current = key;
      checkoutSessionRef.current = data.sessionId;
      checkoutUiModeRef.current = data.uiMode;
      setCheckoutUiMode(data.uiMode);
      setCardFieldComplete(false);
      setStripeClientSecret(data.clientSecret);
      setCheckoutSessionId(data.sessionId);
      setStripeCustomerId(data.customerId);
      if (!email) setEmail(intentEmail);
      try {
        sessionStorage.setItem('xph_trial_checkout', JSON.stringify({
          sessionId: data.sessionId,
          email: intentEmail,
          plan,
          billingCycle: cycle,
          firstName,
          lastName,
        }));
      } catch { /* ignore */ }
    };
    const loadCheckout = async (plan, cycle) => {
      const key = `${intentEmail}:${plan}:${cycle}`;
      const cached = checkoutCacheRef.current[key];
      if (cached) {
        showCached(key, cached);
        return;
      }
      const intentRes = await fetch(`${API_BASE_URL}/subscriptions/create-signup-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: intentEmail,
          name: fullName,
          product: PRODUCT_KEY,
          plan,
          billingCycle: cycle,
        }),
      });
      const intentData = await intentRes.json().catch(() => ({}));
      if (!intentRes.ok) throw new Error(intentData.error || 'Failed to initialize payment');
      if (cancelled) return;
      showCached(key, {
        clientSecret: intentData.clientSecret,
        sessionId: intentData.sessionId,
        uiMode: intentData.uiMode === 'custom' ? 'custom' : 'embedded',
        customerId: intentData.stripeCustomerId,
      });
    };
    (async () => {
      const plan = trialPlanRef.current || TRIAL_DEFAULT_PLAN;
      const cycle = billingCycleRef.current === 'monthly' ? 'monthly' : 'yearly';
      try {
        if (!stripePromise) {
          throw new Error('Payment is not configured on this site. Please contact support.');
        }
        await loadCheckout(plan, cycle);
        const rest = [];
        ['starter', 'professional', 'proMax'].forEach((nextPlan) => {
          ['yearly', 'monthly'].forEach((nextCycle) => {
            if (nextPlan === plan && nextCycle === cycle) return;
            rest.push(loadCheckout(nextPlan, nextCycle).catch(() => undefined));
          });
        });
        await Promise.all(rest);
      } catch (err) {
        if (!cancelled && !checkoutSessionRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to initialize payment');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [
    view,
    enrollAsPartner,
    trialExperiment,
    heldCheckoutSessionId,
    email,
    googleTicket,
    firstName,
    lastName,
  ]);

  useEffect(() => {
    const intentEmail = email || emailFromSignupTicket(googleTicket);
    if (!intentEmail || heldCheckoutRef.current) return;
    const plan = trialPlanRef.current || TRIAL_DEFAULT_PLAN;
    const cycle = billingCycle === 'monthly' ? 'monthly' : 'yearly';
    const cached = checkoutCacheRef.current[`${intentEmail}:${plan}:${cycle}`];
    if (!cached || checkoutSessionRef.current === cached.sessionId) return;
    checkoutKeyRef.current = `${intentEmail}:${plan}:${cycle}`;
    checkoutSessionRef.current = cached.sessionId;
    checkoutUiModeRef.current = cached.uiMode;
    setCheckoutUiMode(cached.uiMode);
    setCardFieldComplete(false);
    setStripeClientSecret(cached.clientSecret);
    setCheckoutSessionId(cached.sessionId);
    setStripeCustomerId(cached.customerId);
  }, [email, googleTicket, selectedPlan, billingCycle]);

  const bindPaymentConfirm = useCallback((fn, token) => {
    if (fn) {
      confirmOwnerRef.current = token || fn;
      confirmPaymentRef.current = fn;
      return;
    }
    if (!token || confirmOwnerRef.current === token) confirmPaymentRef.current = null;
  }, []);

  const handlePaymentStepContinue = async () => {
    if (heldCheckoutRef.current) return true;
    if (heldPaymentRef.current) return true;
    const confirm = confirmPaymentRef.current;
    if (!confirm) return false;
    const confirmed = await confirm();
    if (!confirmed) return false;
    if (checkoutUiModeRef.current === 'custom' && checkoutSessionRef.current) {
      rememberCheckout(checkoutSessionRef.current);
      setError('');
      return true;
    }
    if (heldCheckoutRef.current) return true;
    heldPaymentRef.current = confirmed;
    setHeldPaymentMethodId(confirmed);
    setError('');
    return true;
  };

  const trialCheckoutStarted = useRef(false);
  useEffect(() => {
    if (!trialHandoff || authLoading || !isAuthenticated || !user?.email) return undefined;
    if (trialCheckoutStarted.current) return undefined;
    trialCheckoutStarted.current = true;
    let cancelled = false;
    (async () => {
      try {
        await startStripeSetupForUpgrade({
          product: PRODUCT_KEY,
          planId: trialHandoff,
          planName: PLAN_PRICING[trialHandoff]?.label || trialHandoff,
          billingCycle,
          returnPath: '/subscription',
        });
      } catch (err) {
        if (cancelled) return;
        trialCheckoutStarted.current = false;
        setError(err instanceof Error ? err.message : 'Could not open checkout. Please try again.');
        setTrialHandoff(null);
        setSelectedPlan(trialHandoff);
        setView('signin');
      }
    })();
    return () => { cancelled = true; };
  }, [trialHandoff, authLoading, isAuthenticated, user, billingCycle]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendCooldown]);

  useEffect(() => {
    if (signinMethod !== 'password') return undefined;
    const clearAutofilledValues = () => {
      setEmail('');
      setPassword('');
      if (signinEmailRef.current) signinEmailRef.current.value = '';
      if (signinPasswordRef.current) signinPasswordRef.current.value = '';
    };
    clearAutofilledValues();
    const timer = setTimeout(clearAutofilledValues, 50);
    return () => clearTimeout(timer);
  }, [signinMethod, signinFieldNonce]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get('googleError');
    if (!googleError) return;

    setError(decodeURIComponent(googleError));
    params.delete('googleError');
    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, []);

  const passwordReqs = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  const isPasswordValid = Object.values(passwordReqs).every(Boolean);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const isPhoneValid = /^\+?[\d\s\-()]{10,}$/.test(phone);
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const resetPasswordReqs = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    symbol: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  };
  const isResetPasswordValid = Object.values(resetPasswordReqs).every(Boolean);

  const persistAuthLocally = (token, userData) => {
    if (!token) return;
    localStorage.setItem('token', token);
    localStorage.setItem(API_BASE_STORAGE_KEY, API_BASE_URL.replace(/\/+$/, ''));
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
    localStorage.setItem('currentProduct', PRODUCT_KEY);
  };

  const getRedirectUrl = (token, userData) => {
    // IMPORTANT: keep this payload minimal — token + product only.
    //
    // We previously embedded the full `userData` here so the destination
    // page could hydrate without a follow-up fetch, but the resulting
    // URLs grew long enough (~3–5 KB for accounts with company profile,
    // settings, permissions, etc.) that Cloud Run / the load balancer
    // intermittently dropped them with ERR_CONNECTION_CLOSED before the
    // SPA had a chance to consume them.
    //
    // `persistAuthLocally` (called by every sign-in path immediately
    // before this redirect) has already written the user to localStorage,
    // and AuthContext.consumeAuthPayloadFromCurrentUrl falls back to
    // `initializeAuth()` (token-based GET /auth/me) whenever a payload
    // is token-only — so dropping `user` from the URL is a no-op for
    // the user experience.
    const payload = { token, product: PRODUCT_KEY };
    const authData = encodeURIComponent(JSON.stringify(payload));
    // Partner-only accounts never go to the IN dashboard. They authenticate
    // through the same endpoint, but their landing surface is the standalone
    // Partner Portal which has no IN sidebar / product modules.
    if (userData?.accountType === 'partner') {
      return `/partner-portal?auth=${authData}`;
    }
    // Customer who arrived via the Partner Portal flow (?affiliate=1 or
    // ?go=partner-portal). They have full IN access BUT explicitly asked
    // for the Partner Portal — `partnerMode` captured that intent on
    // mount synchronously, so we treat it as authoritative.
      if (partnerMode) {
      try { sessionStorage.removeItem('xph_post_auth_redirect'); } catch { /* ignore */ }
      try { localStorage.removeItem('xph_post_auth_redirect'); } catch { /* ignore */ }
      return `/partner-portal?lang=${signupLanguage()}&auth=${authData}`;
    }
    // Fallback: a stored post-auth redirect (e.g. set by a different
    // entry point that forwarded through this page). Always consume the
    // flag so the next sign-in lands the user in the default surface.
    // sessionStorage-only as of May 27 2026 — see mount effect.
    try {
      const stored = sessionStorage.getItem('xph_post_auth_redirect');
      if (stored && typeof stored === 'string' && stored.startsWith('/')) {
        sessionStorage.removeItem('xph_post_auth_redirect');
        return `${stored}${stored.includes('?') ? '&' : '?'}lang=${signupLanguage()}&auth=${authData}`;
      }
    } catch { /* ignore */ }
    return `/dashboard?lang=${signupLanguage()}&auth=${authData}`;
  };

  const redirectWithinApp = (targetPath) => {
    navigateOrRedirect(targetPath);
  };

  const getApiBaseCandidates = () => listApiBaseCandidates();

  const isNetworkError = (error) => {
    const message = String(error?.message || '').toLowerCase();
    return message.includes('failed to fetch') || message.includes('network');
  };

  const fetchJsonWithApiFallback = async (path, options = {}) => {
    let lastError = null;
    const bases = getApiBaseCandidates();

    for (const base of bases) {
      try {
        const response = await fetch(`${base}${path}`, options);
        if (response.ok && (!isLocalApiBase(base) || shouldIncludeLocalCandidates())) {
          localStorage.setItem(API_BASE_STORAGE_KEY, base);
        }

        // Retry alternate bases on common mismatch/unavailable statuses.
        if ([401, 403, 404, 500, 502, 503, 504].includes(response.status)) {
          continue;
        }

        let data = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }
        return { response, data, apiBase: base };
      } catch (error) {
        lastError = error;
        if (!isNetworkError(error)) {
          throw error;
        }
      }
    }

    throw lastError || new Error('Failed to fetch');
  };

  const handleSendResetCode = async () => {
    const target = resetVerificationType === 'email' ? forgotEmail : forgotPhone;
    if (resetVerificationType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    if (resetVerificationType === 'phone' && !/^\+?[\d\s\-()]{10,}$/.test(forgotPhone)) {
      setError('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/password-reset/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetVerificationType === 'email' ? forgotEmail : undefined,
          phone: resetVerificationType === 'phone' ? forgotPhone : undefined,
          type: resetVerificationType
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSentTo(data.sentTo || target);
        setView('forgot-code');
      } else {
        setError(data.message || 'Failed to send code');
      }
    } catch {
      setError('Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async () => {
    if (resetCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/password-reset/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetVerificationType === 'email' ? forgotEmail : undefined,
          phone: resetVerificationType === 'phone' ? forgotPhone : undefined,
          code: resetCode
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setView('forgot-newpass');
      } else {
        setError(data.message || 'Invalid code');
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!isResetPasswordValid) {
      setError('Password does not meet requirements');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/password-reset/reset-with-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: resetVerificationType === 'email' ? forgotEmail : undefined,
          phone: resetVerificationType === 'phone' ? forgotPhone : undefined,
          code: resetCode,
          newPassword
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResetSuccess(true);
        setTimeout(() => {
          setResetSuccess(false);
          setView('signin');
          setError('');
          setForgotEmail('');
          setForgotPhone('');
          setResetCode('');
          setNewPassword('');
          setConfirmNewPassword('');
        }, 3000);
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch {
      setError('Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (signinMethod !== 'password') {
      setError('Choose a sign-in method to continue.');
      return;
    }
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { response: res, data, apiBase } = await fetchJsonWithApiFallback('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, loginProduct: PRODUCT_KEY })
      });
      if (!res.ok) {
        if (data.suggestPasswordReset || data.accountLocked) setSuggestPasswordReset(true);
        throw new Error(data.message || 'Login failed');
      }
      if (apiBase) {
        localStorage.setItem(API_BASE_STORAGE_KEY, apiBase);
      }
      setSuggestPasswordReset(false);
      if (data.requires2FA) {
        setRequires2FA(true);
        setTempUserId(data.tempUserId);
        setLoading(false);
        return;
      }
      persistAuthLocally(data.token, data.user);
      redirectWithinApp(getRedirectUrl(data.token, data.user));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      setLoading(false);
    }
  };

  const handleSSOStart = async () => {
    if (signinMethod !== 'sso' && signupEntry !== 'sso') {
      setError('Please choose Continue with SSO first.');
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid work email to continue with SSO.');
      const emailInput = document.getElementById('signin-email');
      if (emailInput) emailInput.focus();
      return;
    }
    setSsoLoading(true);
    setError('');
    try {
      const { response: discoverRes, data: discoverData, apiBase } = await fetchJsonWithApiFallback(
        `/sso/discover?email=${encodeURIComponent(email)}&loginProduct=${encodeURIComponent(PRODUCT_KEY)}`
      );
      if (!discoverRes.ok || !discoverData?.success || !discoverData?.data?.enabled) {
        throw new Error('No SSO provider is configured for this email domain.');
      }
      const returnTo = '/dashboard';
      const desktopFlag = isDesktopRuntime ? '&desktop=1' : '';
      const ssoBase = apiBase || API_BASE_URL;
      window.location.href = `${ssoBase}/sso/start?email=${encodeURIComponent(email)}&loginProduct=${encodeURIComponent(PRODUCT_KEY)}&returnTo=${encodeURIComponent(returnTo)}${desktopFlag}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start SSO');
      setSsoLoading(false);
    }
  };

  const resolvePaidPlanForGoogle = () => {
    if (selectedPlan && isPaidPlan(selectedPlan)) return selectedPlan;
    try {
      const urlPlan = new URLSearchParams(window.location.search).get('plan');
      if (urlPlan && PLAN_KEYS.includes(urlPlan) && isPaidPlan(urlPlan)) {
        return urlPlan;
      }
      const storedPlan = sessionStorage.getItem('xph_signup_selected_plan');
      if (storedPlan && PLAN_KEYS.includes(storedPlan) && isPaidPlan(storedPlan)) {
        return storedPlan;
      }
    } catch { /* ignore */ }
    return null;
  };

  const handleGoogleStart = () => {
    setError('');
    const desktopFlag = isDesktopRuntime ? '&desktop=1' : '';
    // Partner-mode auth pages (signup or signin) send a synthetic
    // `partner-portal` product key so the backend Google callback creates
    // an accountType=partner client (no IN access, auto-enrolled in the
    // affiliate program) and redirects to /partner-portal. Without this
    // override we'd silently grant IN trial access to anyone who hits
    // "Continue with Google" on the Partner Portal sign-in page.
    const googleLoginProduct = partnerMode ? 'partner-portal' : PRODUCT_KEY;
    const params = new URLSearchParams({ loginProduct: googleLoginProduct, lang: signupLanguage() });
    if (desktopFlag) params.set('desktop', '1');
    // Paid plan from pricing/signup — pass through OAuth state so the
    // callback routes into the payment step instead of a free account.
    const paidPlan = resolvePaidPlanForGoogle();
    if (paidPlan) {
      params.set('selectedPlan', paidPlan);
      params.set('billingCycle', billingCycle);
    }
    window.location.href = `${API_BASE_URL}/auth/google/start?${params.toString()}`;
  };

  const handleVerify2FA = async () => {
    if (twoFactorCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempUserId, token: twoFactorCode, loginProduct: PRODUCT_KEY })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Invalid 2FA code');
      if (data.success && data.token) {
        persistAuthLocally(data.token, data.user);
        redirectWithinApp(getRedirectUrl(data.token, data.user));
      } else {
        throw new Error('2FA verification failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA verification failed');
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim()) return setError('First name is required');
    if (!lastName.trim()) return setError('Last name is required');
    if (!isEmailValid) return setError('Please enter a valid email address');
    // Phone + Company are only required for full IN customer signups.
    // Partner-only signups (Partner Program) accept individuals, so we
    // hide those fields and skip their validation entirely.
    if (!enrollAsPartner) {
      if (!phone.trim() || !isPhoneValid) return setError('Please enter a valid phone number (min 10 digits)');
      if (!company.trim()) return setError('Company name is required');
    }
    if (!isPasswordValid) return setError('Password does not meet all requirements');
    if (!passwordsMatch) return setError('Passwords do not match');
    if (!acceptedTerms) return setError(t('auth:validation.termsRequired'));

    setLoading(true);
    try {
      // If the visitor landed here via an affiliate link, the persisted
      // code + click id are merged into the register payload here. Empty
      // object when no referral is in play, so non-referred signups stay
      // untouched.
      const referralPayload = getReferralSignupPayload();
      // Marketing acquisition tags (utm_*) captured on first app touch. Empty
      // object when no campaign is in play, so organic signups stay untouched.
      const attributionPayload = getSignupAttributionPayload();
      // If they arrived from the Partner Program page (?affiliate=1) we
      // mark them as a partner-only account. The backend will skip trial
      // creation, auto-enroll them in the affiliate program, and the
      // frontend will route them to /partner-portal instead of the IN
      // dashboard.
      const accountType = enrollAsPartner ? 'partner' : 'customer';
      // Partner-only signups don't collect phone/company, so we send the
      // matching empty values (the backend's Joi schema marks them
      // optional when accountType === 'partner').
      const registerBody = {
        email,
        password,
        name: `${firstName} ${lastName}`,
        signupProduct: PRODUCT_KEY,
        language: signupLanguage(),
        accountType,
        ...referralPayload,
        ...attributionPayload,
      };
      if (!enrollAsPartner) {
        registerBody.phone = phone;
        registerBody.company = company;
      }
      const regRes = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerBody),
      });
      const regData = await regRes.json();
      if (!regRes.ok) {
        if (regData.code === 'EMAIL_EXISTS') {
          setError(
            regData.hasProductAccess
              ? 'You already have an account. Please sign in instead.'
              : 'An account exists with this email. Please sign in to add this product.'
          );
          setLoading(false);
          return;
        }
        throw new Error(regData.message || 'Registration failed');
      }

      const verRes = await fetch(`${API_BASE_URL}/verification/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          // Only send phone if collected (omitted for partner signups).
          ...(phone ? { phone } : {}),
          name: `${firstName} ${lastName}`,
          type: 'email',
          language: signupLanguage(),
        }),
      });
      const verData = await verRes.json();
      if (!verRes.ok) throw new Error(verData.message || 'Failed to send verification code');

      setResendCooldown(60);
      // V2 (May 28 2026) — multi-screen signup routing:
      //   • Partner / affiliate signup → straight to verification
      //     (they don't pick an IN plan, the backend auto-provisions).
      //   • Deep-link with valid ?plan= in URL → straight to verification
      //     (visitor already confirmed their plan upstream).
      //   • Everything else → dedicated plan picker screen.
      const skipPlanView = enrollAsPartner || trialExperiment || !!selectedPlan;
      setView(skipPlanView ? 'verification' : 'plan');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Plan picker continue ──
  // Called from the dedicated 'plan' view. No backend call — we just
  // persist the choice in component state and move on to the
  // verification code entry.
  const handlePlanContinue = () => {
    if (!selectedPlan) {
      setError('Please pick a plan to continue.');
      return;
    }
    setError('');
    setView('verification');
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/verification/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, phone, name: `${firstName} ${lastName}`, type: 'email', language: signupLanguage() })
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.message || 'Failed to resend');
      }
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    }
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // V2 (May 27 2026) — plan-aware branching matches the marketing
      // site flow exactly:
      //   • Partner-only signup    → backend skips IN subscription and
      //                              auto-enrolls the affiliate account.
      //                              We pass plan: 'free' as a no-op so
      //                              the verify endpoint accepts the body.
      //   • Free IN signup         → /verify creates user + Free sub in
      //                              one call, returns token, we redirect.
      //   • Paid IN signup         → /verify with deferAccountCreation:true
      //                              only verifies the email. Questions come
      //                              next, then the card step for a trial.
      const effectivePlan = enrollAsPartner
        ? 'free'
        : (selectedPlan || 'free');
      // Customer accounts are created only after the signup questions.
      // Partner accounts skip the questionnaire and are created here.
      const deferAccount = !enrollAsPartner;

      const res = await fetch(`${API_BASE_URL}/verification/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, phone, code, type: 'email',
          deferAccountCreation: deferAccount,
          plan: effectivePlan,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Verification failed');

      // Paid plan branch: verification accepted but no user/token yet.
      // Fetch a Stripe SetupIntent and route into the payment view.
      if (deferAccount && data.accountCreated === false) {
        const paidTrial = isPaidPlan(effectivePlan);
        if (trialExperiment && !isPaidPlan(effectivePlan)) setSelectedPlan(TRIAL_DEFAULT_PLAN);
        if (paidTrial && !trialExperiment) {
          if (!stripePromise) {
            throw new Error('Payment is not configured on this site. Please contact support.');
          }
          const fullName = `${firstName} ${lastName}`.trim() || email;
          const intentRes = await fetch(`${API_BASE_URL}/subscriptions/create-signup-intent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, name: fullName, product: PRODUCT_KEY }),
          });
          const intentData = await intentRes.json().catch(() => ({}));
          if (!intentRes.ok) {
            throw new Error(intentData.error || 'Failed to initialize payment');
          }
          setStripeClientSecret(intentData.clientSecret);
          setStripeCustomerId(intentData.stripeCustomerId);
          setView('payment');
          setLoading(false);
          return;
        }
        setView('questions');
        setLoading(false);
        return;
      }

      // Free / partner branch: backend already created the user + free sub
      // (or auto-enrolled the partner). Continue with the existing flow.
      if (data.token && data.user) {
        persistAuthLocally(data.token, data.user);
        // Referral has now been attributed server-side (the /verify route
        // calls affiliateService.attributeReferral after Client.create).
        // Clear the persisted code so a future tab visit doesn't re-stamp
        // a different signup with the same code.
        clearStoredReferral();
        // Acquisition tags have now been stamped onto the account server-side.
        // Clear them so a later signup in this browser isn't mis-attributed.
        clearStoredAttribution();
        try { sessionStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }
        try { localStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }

        // Partner-only signup: backend already auto-enrolled the affiliate
        // account and skipped trial creation. We just need to route them
        // to /partner-portal instead of /dashboard. We bypass the normal
        // getRedirectUrl() (which builds /dashboard?auth=...) and hand-build
        // a payload-bearing partner-portal URL so the SPA still picks up
        // the token on landing.
        const isPartner = data.user?.accountType === 'partner';
        if (isPartner) {
          // Token + product only — see getRedirectUrl for the rationale
          // (URLs with the full user object can exceed Cloud Run / LB
          // limits and produce ERR_CONNECTION_CLOSED). User was already
          // persisted to localStorage by persistAuthLocally above, so
          // the partner-portal SPA hydrate path has everything it needs.
          const partnerPayload = encodeURIComponent(JSON.stringify({
            token: data.token,
            product: PRODUCT_KEY,
          }));
          const partnerTarget = `/partner-portal?auth=${partnerPayload}`;
          setRedirectUrl(partnerTarget);
          setView('success');
          setLoading(false);
          setTimeout(() => {
            redirectWithinApp(partnerTarget);
          }, 2000);
          return;
        }

        // Customer flow: backend /verify already provisioned a Free
        // subscription for this user (see v2 plan policy in
        // backend/src/routes/verification.js). The legacy trial/start
        // call is intentionally NOT invoked here — it would either
        // be a no-op (subscription already exists) or, worse, race
        // /verify and provision a duplicate. We just route to the dashboard.
        redirectWithinApp(getRedirectUrl(data.token, data.user));
        return;
      } else {
        throw new Error('Verification failed - unexpected response');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setLoading(false);
    }
  };

  // ── Complete paid signup after card is saved on Stripe ──
  //
  // Called by PaymentStep once the SetupIntent succeeds and returns a
  // paymentMethodId. We POST to /verification/complete-registration
  // which creates the user, attaches the payment method, and creates a
  // Stripe Subscription on the selected plan + cycle (with the right
  // trial period for ProMax). On success we follow the existing
  // post-signup routing — auth is persisted, then we redirect to the
  // dashboard via the same success view path used by Free signup.
  const handlePaymentComplete = async (paymentMethodId) => {
    if (!selectedPlan) {
      setError('Please go back and choose a plan.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (googleTicket && pendingSignupAnswers) {
        const finRes = await fetch(`${API_BASE_URL}/auth/finalize-google-signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticket: googleTicket,
            onboarding: pendingSignupAnswers,
            paymentMethodId,
            stripeCustomerId,
            selectedPlan,
            billingCycle,
          }),
        });
        const fin = await finRes.json().catch(() => ({}));
        if (!finRes.ok || !fin.token || !fin.user) {
          const failed = new Error(fin.message || 'Failed to create your account');
          failed.code = fin.code || '';
          throw failed;
        }
        if (!fin.needsPayment) {
          persistAuthLocally(fin.token, fin.user);
          localStorage.setItem('newUser', 'true');
          markFirstSignupHome(fin.user?.id || fin.user?.email);
          setPendingGooglePayment(false);
          clearStoredReferral();
          clearStoredAttribution();
          redirectWithinApp(getRedirectUrl(fin.token, fin.user));
          return;
        }
        persistAuthLocally(fin.token, fin.user);
        const upRes = await fetch(`${API_BASE_URL}/subscriptions/complete-google-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${fin.token}`,
          },
          body: JSON.stringify({
            paymentMethodId,
            stripeCustomerId,
            selectedPlan,
            billingCycle,
            product: PRODUCT_KEY,
          }),
        });
        const up = await upRes.json().catch(() => ({}));
        if (!upRes.ok) {
          throw new Error(up.message || 'Failed to start your trial');
        }
        const readyUser = up.user || fin.user;
        persistAuthLocally(fin.token, readyUser);
        localStorage.setItem('newUser', 'true');
        markFirstSignupHome(readyUser?.id || readyUser?.email);
        setPendingGooglePayment(false);
        clearStoredReferral();
        clearStoredAttribution();
        redirectWithinApp(getRedirectUrl(fin.token, readyUser));
        return;
      }

      if (googleTicket && !pendingSignupAnswers) {
        setHeldPaymentMethodId(paymentMethodId);
        setError('');
        setLoading(false);
        setView('questions');
        return;
      }

      if (pendingGooglePayment) {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const res = await fetch(`${API_BASE_URL}/subscriptions/complete-google-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentMethodId,
            stripeCustomerId,
            selectedPlan,
            billingCycle,
            product: PRODUCT_KEY,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || 'Failed to complete signup');
        }
        if (data.user) {
          persistAuthLocally(token, data.user);
          localStorage.setItem('newUser', 'true');
          markFirstSignupHome(data.user?.id || data.user?.email);
          setPendingGooglePayment(false);
          clearStoredReferral();
          clearStoredAttribution();
          redirectWithinApp(getRedirectUrl(token, data.user));
          return;
        }
        throw new Error('Signup completed but no user profile was returned.');
      }

      if (pendingSignupAnswers) {
        const res = await fetch(`${API_BASE_URL}/verification/complete-registration`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            paymentMethodId,
            stripeCustomerId,
            selectedPlan,
            billingCycle,
            onboarding: pendingSignupAnswers,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.token || !data.user) {
          throw new Error(data.message || 'Failed to create your account');
        }
        persistAuthLocally(data.token, data.user);
        localStorage.setItem('newUser', 'true');
        markFirstSignupHome(data.user?.id || data.user?.email);
        clearStoredReferral();
        clearStoredAttribution();
        try { sessionStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }
        try { localStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }
        redirectWithinApp(getRedirectUrl(data.token, data.user));
        return;
      }

      // Card is saved on Stripe, but the account is created only after the
      // last signup question.
      setHeldPaymentMethodId(paymentMethodId);
      setError('');
      setLoading(false);
      setView('questions');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete signup');
      setLoading(false);
    }
  };

  const finishSignupSession = (token, user) => {
    persistAuthLocally(token, user);
    localStorage.setItem('newUser', 'true');
    markFirstSignupHome(user?.id || user?.email);
    clearStoredReferral();
    clearStoredAttribution();
    try { sessionStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }
    try { localStorage.removeItem('xph_signup_join_affiliate'); } catch { /* ignore */ }
    redirectWithinApp(getRedirectUrl(token, user));
  };

  // Last question Continue. This is the first moment a customer account exists.
  const handlePendingQuestionsComplete = async (answers) => {
    const cardId = heldPaymentRef.current || heldPaymentMethodId;
    const sessionId = heldCheckoutRef.current || heldCheckoutSessionId;
    const hasPayment = Boolean(cardId || sessionId);
    const paymentFields = sessionId
      ? { checkoutSessionId: sessionId, stripeCustomerId }
      : { paymentMethodId: cardId, stripeCustomerId };
    const planForSignup = trialExperiment
      ? (trialPlanRef.current || TRIAL_DEFAULT_PLAN)
      : (selectedPlan || 'free');
    if (answers?.startPreference === 'liveDemo') {
      try { window.open('https://www.xpherium.com/contact-sales', '_blank', 'noopener,noreferrer'); } catch { /* ignore */ }
    }
    if (googleTicket && (isPaidPlan(selectedPlan) || trialExperiment) && !hasPayment) {
      if (trialExperiment) {
        throw new Error(t('onboarding:welcomeFlow.payment.required'));
      }
      const ticketEmail = emailFromSignupTicket(googleTicket);
      if (!ticketEmail) {
        throw new Error('Signup session expired. Please start again with Google.');
      }
      if (!stripePromise) {
        throw new Error('Payment is not configured on this site. Please contact support.');
      }
      const intentRes = await fetch(`${API_BASE_URL}/subscriptions/create-signup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: ticketEmail, name: ticketEmail, product: PRODUCT_KEY }),
      });
      const intentData = await intentRes.json().catch(() => ({}));
      if (!intentRes.ok) {
        throw new Error(intentData.error || 'Failed to initialize payment');
      }
      setEmail(ticketEmail);
      setPendingSignupAnswers(answers);
      setPendingGooglePayment(true);
      setStripeClientSecret(intentData.clientSecret);
      setStripeCustomerId(intentData.stripeCustomerId);
      setView('payment');
      return;
    }

    if (googleTicket && hasPayment) {
      const res = await fetch(`${API_BASE_URL}/auth/finalize-google-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket: googleTicket,
          onboarding: answers,
          ...paymentFields,
          selectedPlan: planForSignup,
          billingCycle,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token || !data.user) {
        if (['CHECKOUT_INCOMPLETE', 'CHECKOUT_MISMATCH', 'CARD_NOT_VERIFIED', 'PAYMENT_METHOD_REQUIRED', 'PAYMENT_FAILED'].includes(data.code)) {
          heldCheckoutRef.current = null;
          setHeldCheckoutSessionId(null);
        }
        const failed = new Error(data.message || 'Failed to create your account');
        failed.code = data.code || '';
        throw failed;
      }
      if (data.needsPayment) {
        persistAuthLocally(data.token, data.user);
        const upRes = await fetch(`${API_BASE_URL}/subscriptions/complete-google-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.token}`,
          },
          body: JSON.stringify({
            ...paymentFields,
            selectedPlan: data.selectedPlan || planForSignup,
            billingCycle,
            product: PRODUCT_KEY,
          }),
        });
        const up = await upRes.json().catch(() => ({}));
        if (!upRes.ok) {
          const failed = new Error(up.message || 'Failed to start your trial');
          failed.code = up.code || 'PAYMENT_FAILED';
          throw failed;
        }
        finishSignupSession(data.token, up.user || data.user);
        return;
      }
      finishSignupSession(data.token, data.user);
      return;
    }

    if (googleTicket) {
      const res = await fetch(`${API_BASE_URL}/auth/finalize-google-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket: googleTicket, onboarding: answers }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.token || !data.user) {
        throw new Error(data.message || 'Failed to create your account');
      }
      if (data.needsPayment) {
        persistAuthLocally(data.token, data.user);
        localStorage.setItem('newUser', 'true');
        markFirstSignupHome(data.user?.id || data.user?.email);
        const plan = data.selectedPlan || TRIAL_DEFAULT_PLAN;
        const billing = data.billingCycle === 'monthly' ? 'monthly' : 'yearly';
        const authData = encodeURIComponent(JSON.stringify({ token: data.token, product: PRODUCT_KEY }));
        window.location.assign(`/?view=payment&plan=${encodeURIComponent(plan)}&billing=${billing}&auth=${authData}`);
        return;
      }
      finishSignupSession(data.token, data.user);
      return;
    }

    const paid = !enrollAsPartner && (isPaidPlan(planForSignup) || trialExperiment);
    if (trialExperiment && !isPaidPlan(selectedPlan)) setSelectedPlan(TRIAL_DEFAULT_PLAN);
    if (paid && !hasPayment) {
      if (trialExperiment) {
        throw new Error(t('onboarding:welcomeFlow.payment.required'));
      }
      if (!stripePromise) {
        throw new Error('Payment is not configured on this site. Please contact support.');
      }
      const fullName = `${firstName} ${lastName}`.trim() || email;
      const intentRes = await fetch(`${API_BASE_URL}/subscriptions/create-signup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: fullName, product: PRODUCT_KEY }),
      });
      const intentData = await intentRes.json().catch(() => ({}));
      if (!intentRes.ok) {
        throw new Error(intentData.error || 'Failed to initialize payment');
      }
      setPendingSignupAnswers(answers);
      setStripeClientSecret(intentData.clientSecret);
      setStripeCustomerId(intentData.stripeCustomerId);
      setView('payment');
      return;
    }

    const res = await fetch(`${API_BASE_URL}/verification/complete-registration`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          ...paymentFields,
          selectedPlan: planForSignup,
          billingCycle,
          onboarding: answers,
        }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.token || !data.user) {
      throw new Error(data.message || 'Failed to create your account');
    }
    finishSignupSession(data.token, data.user);
  };

  const handleCodeInput = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1);
    setVerificationCode(newCode);
    if (value && index < 5) {
      const nextEl = document.getElementById(`vc-${index + 1}`);
      if (nextEl) nextEl.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevEl = document.getElementById(`vc-${index - 1}`);
      if (prevEl) prevEl.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    for (let i = 0; i < pasted.length; i += 1) newCode[i] = pasted[i];
    setVerificationCode(newCode);
    const focusEl = document.getElementById(`vc-${Math.min(pasted.length - 1, 5)}`);
    if (focusEl) focusEl.focus();
  };

  if (view === 'questions') {
    const embedCard = trialExperiment && !enrollAsPartner;
    if (embedCard && !trialPlanTouchedRef.current) {
      trialPlanRef.current = TRIAL_DEFAULT_PLAN;
    }
    const cardPlan = embedCard
      ? (trialPlanRef.current || TRIAL_DEFAULT_PLAN)
      : (isPaidPlan(selectedPlan) ? selectedPlan : TRIAL_DEFAULT_PLAN);
    const cardSaved = Boolean(heldCheckoutSessionId || heldPaymentMethodId);
    const intentEmail = email || emailFromSignupTicket(googleTicket);
    const notePlan = PLAN_PRICING[cardPlan];
    const noteYearly = billingCycle !== 'monthly';
    const noteAnnual = formatCharge((notePlan?.yearly || 0) * 12);
    const paymentPlanName = t(`onboarding:welcomeFlow.payment.plans.${cardPlan}`);
    const paymentNote = !embedCard || !notePlan
      ? null
      : noteYearly
        ? t('onboarding:welcomeFlow.payment.disclosureAnnual', {
            plan: paymentPlanName,
            monthly: formatPlanPrice(notePlan.yearly),
            total: noteAnnual,
          })
        : t('onboarding:welcomeFlow.payment.disclosureMonthly', {
            plan: paymentPlanName,
            price: formatPlanPrice(notePlan.monthly),
          });
    return (
      <OnboardingQuestions
        pendingMode={Boolean(googleTicket) || !isAuthenticated}
        onPendingComplete={handlePendingQuestionsComplete}
        paymentReady={embedCard ? Boolean(heldCheckoutSessionId) : false}
        paymentFormReady={embedCard && checkoutUiMode === 'custom' && cardFieldComplete}
        paymentButtonVisible={embedCard && checkoutUiMode === 'custom'}
        onPaymentContinue={embedCard ? handlePaymentStepContinue : null}
        paymentContinueLabel={embedCard ? t('onboarding:welcomeFlow.payment.getPlan') : null}
        paymentNote={paymentNote}
        paymentSlot={embedCard ? (
          <PaymentStep
            localizedCheckout
            email={intentEmail}
            name={`${firstName} ${lastName}`.trim() || intentEmail}
            selectedPlan={cardPlan}
            billingCycle={billingCycle}
            loading={loading}
            error={error}
            onComplete={() => rememberCheckout(checkoutSessionId)}
            onError={setError}
            onCycleChange={selectBillingCycle}
            onPlanChange={selectTrialPlan}
            cardSaved={Boolean(heldCheckoutSessionId)}
            clientSecret={stripeClientSecret}
            uiMode={checkoutUiMode}
            onRegisterConfirm={bindPaymentConfirm}
            onCardComplete={setCardFieldComplete}
          />
        ) : null}
      />
    );
  }

  if (view === 'payment') {
    const leavePayment = () => {
      setError('');
      if (isAuthenticated) logout();
      setView(googleTicket ? 'signin' : 'verification');
    };
    return (
      <div className="min-h-screen bg-white" style={{ colorScheme: 'light' }}>
        <div className="lg:grid lg:grid-cols-[1fr_minmax(420px,37%)] lg:min-h-screen">
          <aside className="relative hidden overflow-hidden bg-white lg:flex lg:flex-col lg:justify-center">
            <div className="relative px-6 py-24 xl:px-10">
              <img
                src="https://www.xpherium.com/videos/hero-film-poster.jpg"
                alt=""
                className="ml-[26%] h-[min(68vh,560px)] w-[74%] object-cover object-[72%_center]"
              />
              <div className="absolute left-6 top-1/2 w-[48%] -translate-y-1/2 bg-white py-8 pr-8 xl:left-10 xl:w-[46%] xl:py-10 xl:pr-10">
                <h2 className="text-[34px] font-normal leading-[1.12] tracking-[-0.02em] text-[#202124] xl:text-[46px]">
                  Turn business data into decisions
                </h2>
                <p className="mt-5 max-w-sm text-[14px] leading-relaxed text-[#5f6368]">
                  A unified AI analytics workspace for insights, dashboards, forecasting, automation, and AI agents.
                </p>
              </div>
            </div>
          </aside>
          <main className="flex min-h-screen items-center justify-center bg-white px-6 py-12">
          <div className="w-full max-w-md">
            {!selectedPlan && (
              <div className="py-8 text-center">
                <p className="text-sm text-slate-600">Choose a plan to start your 14-day trial.</p>
                <button type="button" onClick={leavePayment} className="mt-4 text-sm font-medium text-blue-600">
                  Back
                </button>
              </div>
            )}
            {selectedPlan && !stripeClientSecret && (
              <div className="py-8 text-center">
                {error ? (
                  <>
                    <p className="text-sm text-red-600">{error}</p>
                    <button type="button" onClick={leavePayment} className="mt-4 text-sm font-medium text-blue-600">
                      Back
                    </button>
                  </>
                ) : (
                  <>
                    <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
                    <p className="mt-4 text-sm text-slate-500">Preparing secure checkout…</p>
                  </>
                )}
              </div>
            )}
            {selectedPlan && stripeClientSecret && (
              <PaymentStep
                email={email || user?.email || ''}
                name={`${firstName} ${lastName}`.trim() || user?.name || ''}
                clientSecret={stripeClientSecret}
                selectedPlan={selectedPlan}
                billingCycle={billingCycle}
                loading={loading}
                error={error}
                onSuccess={handlePaymentComplete}
                onError={(message) => setError(message)}
                onCycleChange={setBillingCycle}
                cardTrial={trialExperiment && planHasTrial(selectedPlan)}
                onBack={leavePayment}
              />
            )}
          </div>
          </main>
        </div>
      </div>
    );
  }

  if (trialHandoff) {
    return (
      <div className="relative min-h-screen" style={{ ...SIGNUP_WASH, colorScheme: 'light' }}>
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={SIGNUP_GLOW} />
        <div className="relative z-10 mx-auto mt-16 w-full max-w-md rounded-2xl border border-white/80 bg-white px-6 py-12 text-center shadow-[0_12px_40px_rgba(15,23,42,0.08)]">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <h1 className="mt-4 text-lg font-medium text-slate-900">Opening your 14-day trial</h1>
          <p className="mt-2 text-sm text-slate-600">A card is required. You will not be charged today.</p>
        </div>
      </div>
    );
  }

  if (authLoading || leaveForMarketingSignin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  const doorView = !partnerMode && (view === 'signin' || view === 'signup');
  const openSignup = () => {
    setView('signup');
    setSignupEntry(null);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const choosePlan = (planId) => {
    setSelectedPlan(planId);
    try { sessionStorage.setItem('xph_signup_selected_plan', planId); } catch { /* ignore */ }
    openSignup();
  };

  return (
    <div
      className="min-h-screen bg-[#f4f7fb]"
      style={{ colorScheme: 'light' }}
    >
      {!partnerMode && (
        <AuthTopNav
          mode={view === 'signup' ? 'signup' : 'signin'}
          onSignIn={() => { setView('signin'); setError(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          onCreateAccount={openSignup}
        />
      )}
      <div className={partnerMode
        ? 'w-full lg:grid lg:grid-cols-[1fr_minmax(420px,37%)] lg:min-h-screen'
        : doorView
          ? 'lg:grid lg:min-h-[calc(100vh-3.5rem)] lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)] lg:items-center'
          : 'mx-auto flex w-full max-w-6xl flex-col items-center px-5 pb-16 pt-10 sm:px-8'}
      >
        {partnerMode && (
          <aside className="relative hidden overflow-hidden bg-white lg:flex lg:flex-col lg:justify-between">
            <div className="relative z-10 px-8 pt-10 xl:px-12">
              <a
                href="https://www.xpherium.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Xpherium — visit xpherium.com"
                className="inline-flex w-fit items-center rounded-lg transition-opacity hover:opacity-90"
              >
                <XpheriumLogo variant="dark" size="md" />
              </a>
            </div>

            <div className="relative z-10 flex flex-1 flex-col justify-center px-8 py-8 xl:px-12 xl:py-10">
              <div className="relative">
                <img
                  src={PARTNER_AUTH_HERO}
                  alt={t('partner:auth.heroAlt')}
                  className="w-full object-cover lg:ml-[28%] lg:max-h-[440px]"
                  loading="eager"
                  decoding="async"
                />

                <div className="relative -mt-8 mx-2 bg-white p-6 sm:p-8 lg:absolute lg:left-0 lg:top-1/2 lg:mx-0 lg:mt-0 lg:w-[50%] lg:-translate-y-1/2 lg:p-0 lg:pr-10 lg:py-8 xl:pr-12 shadow-[0_1px_12px_rgba(60,64,67,0.12)] lg:shadow-none">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#1a73e8]">
                    {t('partner:auth.brandLabel')}
                  </p>
                  <h2 className="mt-3 text-[32px] font-normal leading-[1.12] tracking-[-0.02em] text-[#202124] xl:text-[44px]">
                    {view === 'signup' || enrollAsPartner
                      ? t('partner:auth.headlineSignup')
                      : t('partner:auth.headlineSignin')}
                  </h2>
                  <p className="mt-5 max-w-md text-sm leading-relaxed text-[#5f6368] xl:text-[15px]">
                    {view === 'signup' || enrollAsPartner ? (
                      <Trans
                        i18nKey="partner:auth.bodySignup"
                        components={{ strong: <span className="font-medium text-[#202124]" /> }}
                      />
                    ) : (
                      t('partner:auth.bodySignin')
                    )}
                  </p>
                </div>
              </div>
            </div>

            <p className="relative z-10 px-8 pb-8 text-xs text-slate-400 xl:px-12">
              {t('partner:auth.footerTagline')}{' '}
              <a
                href="https://www.xpherium.com/affiliates"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-slate-700 hover:underline underline-offset-2"
              >
                xpherium.com/affiliates
              </a>
            </p>
          </aside>
        )}

        {doorView && (
          <div className="order-2 lg:order-1">
          <AuthHero
            mode={view === 'signup' ? 'signup' : 'signin'}
            onComparePlans={() => document.getElementById('auth-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          />
          </div>
        )}
        <div className={partnerMode
          ? 'flex min-h-screen w-full flex-col justify-center overflow-y-auto bg-white px-5 py-10 sm:px-8 lg:px-10'
          : doorView
            ? 'order-1 w-full bg-white px-6 py-10 sm:px-8 lg:order-2 lg:flex lg:min-h-[calc(100vh-3.5rem)] lg:flex-col lg:justify-center lg:border-l lg:border-slate-200/80 lg:px-10'
            : 'mx-auto my-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white px-6 py-10 shadow-sm'}
        >
          <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
          {/* Product wordmark — swaps to Partner Portal branding whenever
              the visitor arrived through the affiliate flow so this page
              reads as Partner Portal end-to-end instead of feeling like
              "IN with partner messaging bolted on". */}
          {partnerMode ? (
            <div className="mb-8 lg:hidden">
              <a
                href="https://www.xpherium.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Xpherium — visit xpherium.com"
                className="inline-flex items-center rounded-lg transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40"
              >
                <XpheriumLogo variant="dark" size="sm" />
              </a>
              <div className="mt-2.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-500" />
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
                  {t('partner:auth.brandLabel')}
                </p>
              </div>
            </div>
          ) : null}

          {view === 'signin' && !requires2FA && (
            <div>
              <h1 className={`mb-2 text-center font-medium leading-snug text-gray-900 ${partnerMode ? 'text-xl font-bold' : 'text-[1.65rem]'}`}>
                {partnerMode
                  ? t('partner:auth.signinTitle')
                  : 'Welcome back'}
              </h1>
              {!partnerMode && (
                <p className="mb-6 text-center text-[13px] text-slate-500">
                  Log in to Intelligence Navigator
                </p>
              )}

              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <div>
                    {error}
                    {suggestPasswordReset && (
                      <button
                        onClick={() => {
                          setForgotEmail(email);
                          setError('');
                          setView('forgot');
                        }}
                        className={`block mt-1 ${ACCENT.text} hover:underline font-medium`}
                      >
                        Reset your password
                      </button>
                    )}
                  </div>
                </div>
              )}

              <form onSubmit={handleSignIn} autoComplete="off" className="space-y-4">
                <input
                  type="text"
                  name="xph_fake_user"
                  autoComplete="username"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="hidden"
                />
                <input
                  type="password"
                  name="xph_fake_pass"
                  autoComplete="new-password"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="hidden"
                />
                <div className="space-y-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      setSigninMethod('password');
                      setPreventSigninAutofill(true);
                      setEmail('');
                      setPassword('');
                      setShowPassword(false);
                      setSigninFieldNonce((prev) => prev + 1);
                      setError('');
                    }}
                    className={`flex w-full items-center justify-center gap-2.5 rounded-full border py-3 text-[14px] font-medium transition-colors ${
                      signinMethod === 'password'
                        ? 'border-gray-400 bg-gray-50 text-gray-900'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Mail className="h-4 w-4 text-gray-500" />
                    Continue with password
                  </button>

                  <button
                    type="button"
                    onClick={handleGoogleStart}
                    className="flex w-full items-center justify-center gap-2.5 rounded-full border border-gray-300 bg-white py-3 text-[14px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <GoogleIcon />
                    Continue with Google
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSigninMethod('sso');
                      setPreventSigninAutofill(true);
                      setError('');
                    }}
                    className={`flex w-full items-center justify-center gap-2.5 rounded-full border py-3 text-[14px] font-medium transition-colors ${
                      signinMethod === 'sso'
                        ? 'border-gray-400 bg-gray-50 text-gray-900'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Building className="h-4 w-4 text-gray-500" />
                    Continue with SSO
                  </button>
                </div>

                {signinMethod && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          key={`signin-email-${signinFieldNonce}`}
                          ref={signinEmailRef}
                          id="signin-email"
                          type="email"
                          name={`xph_signin_email_${signinFieldNonce}`}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onFocus={() => setPreventSigninAutofill(false)}
                          placeholder={signinMethod === 'password' ? 'Enter your email' : 'Enter your work email'}
                          autoComplete="off"
                          data-lpignore="true"
                          data-1p-ignore="true"
                          readOnly={preventSigninAutofill}
                          className={`w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400`}
                          required
                        />
                      </div>
                    </div>

                    {signinMethod === 'password' ? (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                          <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              key={`signin-password-${signinFieldNonce}`}
                              ref={signinPasswordRef}
                              type={showPassword ? 'text' : 'password'}
                              name={`xph_signin_password_${signinFieldNonce}`}
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              onFocus={() => setPreventSigninAutofill(false)}
                              placeholder="Enter your password"
                              autoComplete="new-password"
                              data-lpignore="true"
                              data-1p-ignore="true"
                              readOnly={preventSigninAutofill}
                              className={`w-full pl-12 pr-12 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400`}
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                          <div className="flex justify-end mt-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                setForgotEmail(email);
                                setError('');
                                setView('forgot');
                              }}
                              className={`text-sm ${ACCENT.text} ${ACCENT.textHover} font-medium`}
                            >
                              Forgot Password?
                            </button>
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className={`w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r ${ACCENT.gradient} ${ACCENT.gradientHover} disabled:opacity-50 transition-all shadow-lg ${ACCENT.shadow} flex items-center justify-center gap-2 text-[15px]`}
                        >
                          {loading && <Loader2 className="w-4 h-4 animate-spin" />}Sign In
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="p-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 text-sm">
                          SSO selected. You will authenticate with your company credentials.
                        </div>
                        <button
                          type="button"
                          onClick={handleSSOStart}
                          disabled={loading || ssoLoading}
                          className="w-full py-3.5 rounded-xl font-semibold text-gray-900 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-all flex items-center justify-center gap-2 text-[15px]"
                        >
                          {ssoLoading && <Loader2 className="w-4 h-4 animate-spin" />}Continue with SSO
                        </button>
                      </>
                    )}
                  </>
                )}
              </form>

              <div className="my-6 h-px w-full bg-gray-200" />

              <p className="text-center text-gray-500 text-sm">
                {partnerMode ? "Don't have an Account? " : 'New to Intelligence Navigator? '}
                <button onClick={() => { setView('signup'); setSignupEntry(null); setError(''); }} className={`${ACCENT.text} ${ACCENT.textHover} font-semibold`}>
                  {partnerMode ? 'Sign Up' : 'Create a free account'}
                </button>
              </p>
            </div>
          )}

          {view === 'signin' && requires2FA && (
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h1>
              <p className="text-gray-500 mb-7">Enter the 6-digit code from your authenticator app.</p>
              {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
              <input type="text" inputMode="numeric" maxLength={6} value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" className={`w-full px-4 py-3.5 text-center text-2xl tracking-[0.5em] bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 caret-gray-900 placeholder:text-gray-300 mb-5 font-mono`} />
              <button onClick={handleVerify2FA} disabled={loading || twoFactorCode.length !== 6} className={`w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r ${ACCENT.gradient} ${ACCENT.gradientHover} disabled:opacity-50 transition-all shadow-lg ${ACCENT.shadow} flex items-center justify-center gap-2`}>
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}Verify
              </button>
              <button onClick={() => { setRequires2FA(false); setTwoFactorCode(''); setError(''); }} className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700">&larr; Back to Sign In</button>
            </div>
          )}

          {view === 'signup' && (
            <div>
              {(partnerMode || enrollAsPartner) && (
              <h1 className="mb-5 text-center text-xl font-bold leading-snug text-gray-900">
                {partnerMode
                  ? t('partner:auth.signupTitlePortal')
                  : t('partner:auth.signupTitleProgram')}
              </h1>
              )}
              {(partnerMode || enrollAsPartner) ? (
                <p
                  className={
                    partnerMode
                      ? 'mb-5 text-center text-sm leading-relaxed text-gray-500'
                      : 'text-gray-500 mb-5 leading-relaxed'
                  }
                >
                  {t('partner:auth.signupSubtitle')}
                </p>
              ) : (
                <>
                  <h1 className="mb-2 text-center text-[1.55rem] font-medium leading-snug text-gray-900">
                    {trialExperiment ? t('auth:trial.primaryCta') : 'Start using Intelligence Navigator free'}
                  </h1>
                  <p className="mb-5 text-center text-[13px] leading-relaxed text-slate-500">
                    {trialExperiment ? t('auth:trial.signupSupport') : 'No credit card required. Upgrade anytime.'}
                  </p>
                </>
              )}
              {referralCode && (
                <div className="flex items-start gap-2.5 p-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm">
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">You're signing up with a referral.</div>
                    <div className="text-emerald-700/80 mt-0.5">
                      Code <span className="font-mono font-semibold">{referralCode}</span> is attached to your account.
                    </div>
                  </div>
                </div>
              )}
              {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}

              {!partnerMode && !enrollAsPartner && !signupEntry && (
                <div className="space-y-2.5">
                  <button type="button" onClick={handleGoogleStart} className="flex w-full items-center justify-center gap-2.5 rounded-full border border-gray-300 bg-white py-3 text-[14px] font-medium text-gray-700 transition-colors hover:bg-gray-50">
                    <GoogleIcon />
                    Continue with Google
                  </button>
                  <button type="button" onClick={() => { setSignupEntry('email'); setError(''); }} className="flex w-full items-center justify-center gap-2.5 rounded-full border border-gray-300 bg-white py-3 text-[14px] font-medium text-gray-700 transition-colors hover:bg-gray-50">
                    <Mail className="h-4 w-4 text-gray-500" />
                    Continue with email
                  </button>
                  <button type="button" onClick={() => { setSignupEntry('sso'); setError(''); }} className="flex w-full items-center justify-center gap-2.5 rounded-full border border-gray-300 bg-white py-3 text-[14px] font-medium text-gray-700 transition-colors hover:bg-gray-50">
                    <Building className="h-4 w-4 text-gray-500" />
                    Continue with SSO
                  </button>
                  <p className="pt-3 text-center text-[12px] leading-relaxed text-slate-400">
                    {trialExperiment ? t('auth:trial.signupSupport') : 'No credit card required · Start free · Upgrade anytime'}
                  </p>
                  <button type="button" onClick={() => document.getElementById('auth-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="w-full text-center text-[13px] font-medium text-sky-700 hover:text-sky-800">
                    Compare plans
                  </button>
                </div>
              )}

              {!partnerMode && !enrollAsPartner && signupEntry === 'sso' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Work email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900" />
                  <button type="button" onClick={handleSSOStart} disabled={ssoLoading} className="flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 bg-white py-3 text-[14px] font-medium text-gray-900 hover:bg-gray-50">
                    {ssoLoading && <Loader2 className="w-4 h-4 animate-spin" />}Continue with SSO
                  </button>
                  <button type="button" onClick={() => setSignupEntry(null)} className="text-[13px] text-slate-500">Back</button>
                </div>
              )}

              {(partnerMode || enrollAsPartner || signupEntry === 'email') && (
              <form onSubmit={handleSignUp} className="space-y-3.5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{enrollAsPartner ? t('auth:firstName') : 'First Name'}</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={enrollAsPartner ? t('auth:firstName') : 'First name'} className={`w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm`} required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{enrollAsPartner ? t('auth:lastName') : 'Last Name'}</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={enrollAsPartner ? t('auth:lastName') : 'Last name'} className={`w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm`} required />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{enrollAsPartner ? t('auth:email') : 'Email'}</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={enrollAsPartner ? t('auth:placeholders.enterEmail') : 'you@company.com'} className={`w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm`} required />
                  </div>
                </div>

                {/* Phone and Company are only collected for full IN
                    (customer) signups. Partner-only signups can be
                    individuals — we omit these fields entirely so the
                    Partner Program form stays clean. */}
                {!enrollAsPartner && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <div className="relative">
                        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" className={`w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm`} required />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                      <div className="relative">
                        <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name" className={`w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm`} required />
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{enrollAsPartner ? t('auth:password') : 'Password'}</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={enrollAsPartner ? t('auth:placeholders.enterPassword') : 'Create a password'} className={`w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm`} required />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                  {password && (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1.5">
                      {[
                        { ok: passwordReqs.length, label: enrollAsPartner ? t('auth:passwordReq.length') : '8+ characters' },
                        { ok: passwordReqs.uppercase, label: enrollAsPartner ? t('auth:passwordReq.uppercase') : '1 uppercase' },
                        { ok: passwordReqs.number, label: enrollAsPartner ? t('auth:passwordReq.number') : '1 number' },
                        { ok: passwordReqs.symbol, label: enrollAsPartner ? t('auth:passwordReq.symbol') : '1 symbol' }
                      ].map((r, i) => (
                        <p key={i} className={`text-xs flex items-center gap-1 ${r.ok ? 'text-green-600' : 'text-gray-400'}`}>
                          {r.ok ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-gray-300 inline-block" />} {r.label}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{enrollAsPartner ? t('auth:confirmPassword') : 'Confirm Password'}</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={enrollAsPartner ? t('auth:forgot.confirmPasswordPlaceholder') : 'Confirm your password'} className={`w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm`} required />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                  {confirmPassword && !passwordsMatch && <p className="text-xs text-red-500 mt-1">{enrollAsPartner ? t('auth:validation.passwordsMismatch') : 'Passwords do not match'}</p>}
                </div>

                <div>
                  <label className="flex items-start gap-2 cursor-pointer pt-1">
                    <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    <span className="text-xs text-gray-500">
                      {t('auth:legal.iAgreeTo')}{' '}
                      <button type="button" onClick={() => setShowTerms(!showTerms)} className={`${ACCENT.text} hover:underline inline-flex items-center gap-0.5`}>
                        {t('auth:legal.termsOfService')} <ChevronDown className={`w-3 h-3 transition-transform ${showTerms ? 'rotate-180' : ''}`} />
                      </button>{' '}
                      {t('auth:legal.andConnector')}{' '}
                      <button type="button" onClick={() => setShowPrivacy(!showPrivacy)} className={`${ACCENT.text} hover:underline inline-flex items-center gap-0.5`}>
                        {t('auth:legal.privacyPolicy')} <ChevronDown className={`w-3 h-3 transition-transform ${showPrivacy ? 'rotate-180' : ''}`} />
                      </button>
                    </span>
                  </label>
                  {showTerms && (
                    <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 leading-relaxed">
                      <p className="font-semibold text-gray-800 mb-1">{t('auth:legal.termsOfService')}</p>
                      <p>{t('auth:legal.termsBody')}</p>
                      <p className="mt-2">{t('auth:legal.fullTermsPrefix')} <a href="https://www.xpherium.com/terms" target="_blank" rel="noopener noreferrer" className={`${ACCENT.text} hover:underline`}>xpherium.com/terms</a>.</p>
                    </div>
                  )}
                  {showPrivacy && (
                    <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 leading-relaxed">
                      <p className="font-semibold text-gray-800 mb-1">{t('auth:legal.privacyPolicy')}</p>
                      <p>{t('auth:legal.privacyBody')}</p>
                      <p className="mt-2">{t('auth:legal.fullPrivacyPrefix')} <a href="https://www.xpherium.com/privacy" target="_blank" rel="noopener noreferrer" className={`${ACCENT.text} hover:underline`}>xpherium.com/privacy</a>.</p>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r ${ACCENT.gradient} ${ACCENT.gradientHover} disabled:opacity-50 transition-all shadow-lg ${ACCENT.shadow} flex items-center justify-center gap-2 text-[15px] mt-1`}
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {enrollAsPartner ? t('partner:auth.createPartnerAccount') : 'Continue'}
                </button>
              </form>
              )}

              <p className="text-center text-gray-500 mt-4 text-sm">
                {t('auth:alreadyHaveAccount')}{' '}
                <button onClick={() => { setView('signin'); setError(''); }} className={`${ACCENT.text} ${ACCENT.textHover} font-semibold`}>
                  {t('auth:signIn')}
                </button>
              </p>
            </div>
          )}

          {view === 'forgot' && (
            <div>
              <button onClick={() => { setView('signin'); setError(''); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-5">
                <ArrowLeft className="w-4 h-4" /> Back to Sign In
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Reset Your Password</h1>
              <p className="text-gray-500 mb-6">Enter your email or phone to receive a verification code.</p>
              {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Send code via</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setResetVerificationType('email')} className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${resetVerificationType === 'email' ? `bg-gradient-to-r ${ACCENT.gradient} text-white shadow-md` : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    <Mail className="w-4 h-4" /> Email
                  </button>
                  <button type="button" onClick={() => setResetVerificationType('phone')} className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${resetVerificationType === 'phone' ? `bg-gradient-to-r ${ACCENT.gradient} text-white shadow-md` : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    <Phone className="w-4 h-4" /> SMS
                  </button>
                </div>
              </div>

              {resetVerificationType === 'email' ? (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" className={`w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400`} />
                  </div>
                </div>
              ) : (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="tel" value={forgotPhone} onChange={(e) => setForgotPhone(e.target.value)} placeholder="+1 (555) 123-4567" className={`w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400`} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Enter the phone number associated with your account</p>
                </div>
              )}

              <button onClick={handleSendResetCode} disabled={loading} className={`w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r ${ACCENT.gradient} ${ACCENT.gradientHover} disabled:opacity-50 transition-all shadow-lg ${ACCENT.shadow} flex items-center justify-center gap-2 text-[15px]`}>
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}Send Verification Code
              </button>
            </div>
          )}

          {view === 'forgot-code' && (
            <div>
              <button onClick={() => { setView('forgot'); setError(''); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-5">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Verify Your Identity</h1>
              <p className="text-gray-500 mb-5">Enter the 6-digit code sent to <span className="font-medium text-gray-700">{sentTo}</span>.</p>
              {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
              <div className="mb-5">
                <input type="text" inputMode="numeric" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} placeholder="000000" className={`w-full px-4 py-4 text-center text-2xl tracking-[0.4em] font-mono bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 caret-gray-900 placeholder:text-gray-300`} />
              </div>
              <button onClick={handleVerifyResetCode} disabled={loading || resetCode.length !== 6} className={`w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r ${ACCENT.gradient} ${ACCENT.gradientHover} disabled:opacity-50 transition-all shadow-lg ${ACCENT.shadow} flex items-center justify-center gap-2 text-[15px]`}>
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}Verify Code
              </button>
              <button onClick={handleSendResetCode} disabled={loading} className={`w-full mt-3 text-sm ${ACCENT.text} ${ACCENT.textHover} font-medium`}>
                Resend code
              </button>
            </div>
          )}

          {view === 'forgot-newpass' && (
            <div>
              {resetSuccess ? (
                <div className="text-center py-8">
                  <div className={`w-16 h-16 rounded-full ${ACCENT.bg} flex items-center justify-center mx-auto mb-5`}>
                    <Check className={`w-8 h-8 ${ACCENT.text}`} />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Reset Successful</h1>
                  <p className="text-gray-500">You can now sign in with your new password.</p>
                </div>
              ) : (
                <>
                  <button onClick={() => { setView('forgot-code'); setError(''); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-5">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Create New Password</h1>
                  <p className="text-gray-500 mb-6">Choose a strong password for your account.</p>
                  {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Create a strong password" className={`w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400`} />
                        <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New Password</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input type={showConfirmNewPassword ? 'text' : 'password'} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder="Confirm your password" className={`w-full pl-12 pr-12 py-3 border ${confirmNewPassword && newPassword !== confirmNewPassword ? 'border-red-300' : confirmNewPassword && newPassword === confirmNewPassword ? 'border-green-300' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 placeholder:text-gray-400`} />
                        <button type="button" onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showConfirmNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                      </div>
                    </div>
                  </div>

                  <button onClick={handleResetPassword} disabled={loading || !isResetPasswordValid || newPassword !== confirmNewPassword} className={`w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r ${ACCENT.gradient} ${ACCENT.gradientHover} disabled:opacity-50 transition-all shadow-lg ${ACCENT.shadow} flex items-center justify-center gap-2 text-[15px] mt-5`}>
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}Reset Password
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── PLAN PICKER (dedicated screen) ──
              Rendered between the signup form and the email verification
              step so the plan choice gets its own focused screen with
              larger cards. Partner signups and visitors arriving via
              `?plan=` deep-link skip this view entirely. */}
          {view === 'plan' && (
            <div>
              <button
                onClick={() => { setView('signup'); setError(''); }}
                type="button"
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-5"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Choose your plan</h1>
              <p className="text-gray-500 mb-5 leading-relaxed">
                Pick the plan that fits your team. You can upgrade or downgrade anytime.
              </p>

              {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}

              {/* Billing-cycle toggle (right-aligned, compact). */}
              <div className="flex items-center justify-end mb-3">
                <div className="inline-flex bg-gray-100 rounded-full p-0.5 text-[11px]">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`px-3 py-1 rounded-full transition-colors ${
                      billingCycle === 'yearly'
                        ? 'bg-white text-gray-900 shadow-sm font-semibold'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Yearly <span className="text-emerald-600 font-semibold">up to −20%</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-3 py-1 rounded-full transition-colors ${
                      billingCycle === 'monthly'
                        ? 'bg-white text-gray-900 shadow-sm font-semibold'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* 2-column grid of larger cards. Each card shows the
                  plan name, headline price, tagline and 3 feature
                  bullets. Click anywhere on the card to select. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PLAN_KEYS.filter((plan) => !(trialExperiment && plan === 'free')).map((plan) => {
                  const meta = PLAN_PRICING[plan];
                  const isSelectedCard = selectedPlan === plan;
                  const isPopular = plan === 'professional';
                  const price = billingCycle === 'yearly' ? meta.yearly : meta.monthly;
                  const trialPlan = planHasTrial(plan);
                  return (
                    <button
                      key={plan}
                      type="button"
                      onClick={() => { setSelectedPlan(plan); setError(''); }}
                      aria-pressed={isSelectedCard}
                      className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                        isSelectedCard
                          ? 'border-indigo-600 bg-indigo-50/40 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-sm'
                      }`}
                    >
                      {isPopular && !isSelectedCard && (
                        <span className="absolute -top-2 left-3 px-2 py-0.5 text-[9px] font-bold rounded uppercase tracking-wider bg-indigo-600 text-white shadow-sm">
                          Popular
                        </span>
                      )}
                      {isSelectedCard && (
                        <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" strokeWidth={3} />
                        </span>
                      )}

                      <p className="text-sm font-semibold text-gray-900">{meta.label}</p>
                      <div className="mt-1 mb-1.5">
                        <span className="text-3xl font-bold text-gray-900 leading-none">
                          ${price}
                        </span>
                        <span className="text-xs font-normal text-gray-500 ml-1">/mo</span>
                        {trialPlan && (
                          <span className="block text-[11px] text-emerald-600 font-medium mt-1">
                            {trialExperiment ? t('auth:trial.badge') : '14-day trial'}
                          </span>
                        )}
                        {!trialPlan && plan !== 'free' && billingCycle === 'yearly' && (
                          <span className="block text-[11px] text-gray-400 mt-1">
                            billed annually
                          </span>
                        )}
                        {plan === 'free' && (
                          <span className="block text-[11px] text-gray-400 mt-1">
                            no credit card
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3 leading-snug">
                        {PLAN_TAGLINES[plan]}
                      </p>
                      <ul className="space-y-1.5">
                        {PLAN_HIGHLIGHTS[plan].map((feat) => (
                          <li key={feat} className="text-[11px] text-gray-700 flex items-start gap-1.5 leading-snug">
                            <Check className={`w-3 h-3 mt-0.5 flex-shrink-0 ${isSelectedCard ? 'text-indigo-600' : 'text-gray-400'}`} strokeWidth={2.5} />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-gray-500 mt-3 leading-snug">
                Need more? <a href="https://www.xpherium.com/pricing#enterprise" target="_blank" rel="noopener noreferrer" className={`${ACCENT.text} ${ACCENT.textHover} font-medium underline-offset-2 hover:underline`}>Custom / Enterprise →</a>
              </p>

              <button
                onClick={handlePlanContinue}
                disabled={!selectedPlan || loading}
                type="button"
                className={`w-full mt-5 py-3 rounded-xl font-semibold text-white bg-gradient-to-r ${ACCENT.gradient} ${ACCENT.gradientHover} disabled:opacity-50 transition-all shadow-lg ${ACCENT.shadow} flex items-center justify-center gap-2 text-[15px]`}
              >
                {!selectedPlan
                  ? 'Pick a plan to continue'
                  : selectedPlan === 'free'
                  ? 'Continue with Free'
                  : planHasTrial(selectedPlan)
                  ? `Continue — start ${PLAN_PRICING[selectedPlan].label} trial`
                  : `Continue to payment — $${
                      billingCycle === 'yearly'
                        ? PLAN_PRICING[selectedPlan].yearly
                        : PLAN_PRICING[selectedPlan].monthly
                    }/mo`}
              </button>
            </div>
          )}

          {view === 'verification' && (
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
              <p className="text-gray-500 mb-7 leading-relaxed">
                We sent a 6-digit code to <span className="font-medium text-gray-700">{email}</span>. Enter it below to
                {partnerMode ? t('partner:auth.verificationSuffix') : ' verify your account.'}
              </p>
              {error && <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
              <div className="flex gap-2.5 justify-center mb-6">
                {verificationCode.map((digit, i) => (
                  <input
                    key={i}
                    id={`vc-${i}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeInput(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    onPaste={i === 0 ? handleCodePaste : undefined}
                    inputMode="numeric"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    className={`w-12 h-14 text-center text-xl font-bold bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ACCENT.ring} focus:border-transparent text-gray-900 caret-gray-900 transition-all`}
                  />
                ))}
              </div>
              <button onClick={handleVerifyCode} disabled={loading || verificationCode.join('').length !== 6} className={`w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r ${ACCENT.gradient} ${ACCENT.gradientHover} disabled:opacity-50 transition-all shadow-lg ${ACCENT.shadow} flex items-center justify-center gap-2 text-[15px]`}>
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}Verify & Continue
              </button>
              <div className="text-center mt-5">
                <p className="text-sm text-gray-500">
                  Didn&apos;t receive the code?{' '}
                  <button onClick={handleResend} disabled={resendCooldown > 0} className={`${ACCENT.text} ${ACCENT.textHover} font-medium disabled:text-gray-400 disabled:cursor-not-allowed`}>
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                  </button>
                </p>
                <button
                  onClick={() => {
                    // Partner signups never visit the plan picker, so
                    // "Back" returns them to the credentials form. IN
                    // signups go back one step to plan selection so
                    // they can change their mind without re-entering
                    // their email/password.
                    setView(enrollAsPartner ? 'signup' : 'plan');
                    setError('');
                    setVerificationCode(['', '', '', '', '', '']);
                  }}
                  className="text-sm text-gray-400 hover:text-gray-600 mt-2"
                >
                  &larr; Back
                </button>
              </div>
            </div>
          )}

          {view === 'success' && (
            <div className="text-center py-8">
              <div className={`w-16 h-16 rounded-full ${partnerMode ? 'bg-cyan-50' : ACCENT.bg} flex items-center justify-center mx-auto mb-5`}>
                <Check className={`w-8 h-8 ${partnerMode ? 'text-cyan-600' : ACCENT.text}`} />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {partnerMode ? t('partner:auth.welcomeTitle') : `Welcome to ${PRODUCT_NAME}!`}
              </h1>
              <p className="text-gray-500 mb-6">
                {/* Success copy is plan-aware for IN signups, fixed
                    for partner signups. The null branch is purely
                    defensive — success view is only reachable after a
                    plan was processed (or partner flow completed). */}
                {partnerMode
                  ? t('partner:auth.welcomeBody')
                  : !selectedPlan
                  ? 'Your account is ready.'
                  : selectedPlan === 'free'
                  ? 'Your account is ready on the Free plan.'
                  : planHasTrial(selectedPlan)
                  ? (trialExperiment ? t('auth:trial.started') : 'Your account is ready and your 14-day trial has started.')
                  : `Your ${PLAN_PRICING[selectedPlan].label} subscription is now active.`}
              </p>
              <p className="text-sm text-gray-400 mb-4">
                {partnerMode
                  ? t('partner:auth.redirecting')
                  : 'Redirecting to your dashboard in a few seconds...'}
              </p>
              {redirectUrl && (
                <button
                  type="button"
                  onClick={() => redirectWithinApp(redirectUrl)}
                  className={`text-sm ${partnerMode ? 'text-cyan-700 hover:text-cyan-800' : `${ACCENT.text} ${ACCENT.textHover}`} font-medium hover:underline`}
                >
                  Click here if not redirected
                </button>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {!partnerMode && (view === 'signin' || view === 'signup') && (
        <AuthBelowFold prominent={view === 'signup'} onChoosePlan={choosePlan} />
      )}

      <div className={`${partnerMode ? 'fixed' : 'relative mt-8'} bottom-4 left-0 right-0 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 pb-8 text-xs text-gray-400 tracking-wide pointer-events-none`}>
        <span>&copy; {new Date().getFullYear()} Xpherium. All rights reserved.</span>
        <span className="text-gray-300">·</span>
        <a
          href="https://www.xpherium.com"
          target="_blank"
          rel="noopener noreferrer"
          className="pointer-events-auto hover:text-indigo-700 transition-colors inline-flex items-center gap-1"
        >
          <Globe className="w-3 h-3" />
          www.xpherium.com
        </a>
        {!partnerMode && (
          <>
            <span className="text-gray-300">·</span>
            <a
              href="/affiliates"
              className="pointer-events-auto hover:text-indigo-700 transition-colors inline-flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              Partner Program — Earn up to 20%
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default LandingPage;